import React, { useEffect, useMemo, useRef, useState } from "react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import heroImage from "../assets/img/it-banner.png";
import schools from "../assets/schools-data";
import Slider from "react-slick";
import recruiters from "../school-components/Recruiters"
import Faculty from "../school-components/Faculty";
import Alumni from "../home-components/Alumni";

const Arrow = ({ diagonal = false }) => (
    <svg
        viewBox="0 0 24 24"
        className="h-5 w-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        aria-hidden="true"
    >
        {diagonal ? (
            <path d="M7 17 17 7M8 7h9v9" />
        ) : (
            <path d="M5 12h14M13 6l6 6-6 6" />
        )}
    </svg>
);

const ChipIcon = () => (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="6" y="6" width="12" height="12" rx="2" />
        <path d="M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M18 9h4M2 15h4M18 15h4M9.5 9.5h5v5h-5z" />
    </svg>
);

const CloudIcon = () => (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M6.5 19h11a4.5 4.5 0 0 0 .7-8.94A7 7 0 0 0 4.75 8.5 5.25 5.25 0 0 0 6.5 19Z" />
    </svg>
);

const ShieldIcon = () => (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 3 20 6v6c0 5-3 8-8 10-5-2-8-5-8-10V6l8-3Z" />
        <path d="m9 12 2 2 4-4" />
    </svg>
);

const DatabaseIcon = () => (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.5">
        <ellipse cx="12" cy="5" rx="8" ry="3" />
        <path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
    </svg>
);

const focusAreas = [
    { number: "01", title: "Artificial Intelligence", icon: <ChipIcon /> },
    { number: "02", title: "Cloud & DevOps", icon: <CloudIcon /> },
    { number: "03", title: "Cybersecurity", icon: <ShieldIcon /> },
    { number: "04", title: "Data Science", icon: <DatabaseIcon /> },
];

const networkNodes = [
    ["12%", "18%"],
    ["27%", "8%"],
    ["42%", "23%"],
    ["62%", "10%"],
    ["80%", "25%"],
    ["18%", "48%"],
    ["38%", "57%"],
    ["59%", "43%"],
    ["77%", "60%"],
    ["91%", "45%"],
    ["26%", "82%"],
    ["52%", "75%"],
    ["72%", "88%"],
];

const learningStages = [
    {
        number: "01",
        label: "Discover",
        title: "Build strong foundations",
        short: "Computing fundamentals",
        description:
            "Students begin with programming, computing systems, mathematics and problem-solving principles that establish a strong technical foundation.",
        highlights: [
            "Programming fundamentals",
            "Computational thinking",
            "Data structures",
        ],
        icon: "code",
    },
    {
        number: "02",
        label: "Experiment",
        title: "Learn inside technology labs",
        short: "Practice-led education",
        description:
            "Concepts are transformed into working solutions through coding laboratories, cloud environments, simulations and guided technical experiments.",
        highlights: [
            "Coding laboratories",
            "Cloud-based environments",
            "AI and data experiments",
        ],
        icon: "lab",
    },
    {
        number: "03",
        label: "Create",
        title: "Solve meaningful problems",
        short: "Project-based learning",
        description:
            "Multidisciplinary projects challenge students to design applications, analyse data and develop solutions for relevant social and industry problems.",
        highlights: [
            "Live problem statements",
            "Team-based development",
            "Product prototyping",
        ],
        icon: "project",
    },
    {
        number: "04",
        label: "Collaborate",
        title: "Learn from industry",
        short: "Professional exposure",
        description:
            "Industry mentors, expert sessions, internships and collaborative challenges connect academic learning with current professional practices.",
        highlights: [
            "Industry mentorship",
            "Internship exposure",
            "Expert masterclasses",
        ],
        icon: "network",
    },
    {
        number: "05",
        label: "Innovate",
        title: "Research emerging technology",
        short: "Research and innovation",
        description:
            "Students investigate Artificial Intelligence, cybersecurity, cloud computing, data science and other emerging domains through guided research.",
        highlights: [
            "Faculty-guided research",
            "Innovation challenges",
            "Emerging technologies",
        ],
        icon: "innovation",
    },
    {
        number: "06",
        label: "Launch",
        title: "Become industry ready",
        short: "Career preparation",
        description:
            "Capstone projects, hackathons, portfolio development and placement preparation help students confidently transition into careers or higher studies.",
        highlights: [
            "Professional portfolio",
            "Hackathons and competitions",
            "Career preparation",
        ],
        icon: "launch",
    },
];

const schoolOutcomes = [
    {
        number: "01",
        title: "Technology careers",
        description:
            "Prepared for opportunities across software development, IT services, cloud operations, data analytics, cybersecurity and digital consulting.",
        tag: "Career pathways",
        icon: "career",
    },
    {
        number: "02",
        title: "Industry readiness",
        description:
            "Capable of working with development workflows, collaborative tools, documentation standards and modern technology environments.",
        tag: "Professional capability",
        icon: "industry",
    },
    {
        number: "03",
        title: "Innovation mindset",
        description:
            "Equipped to investigate problems, test ideas and transform technical knowledge into thoughtful digital solutions.",
        tag: "Problem solving",
        icon: "innovation",
    },
    {
        number: "04",
        title: "Entrepreneurial thinking",
        description:
            "Encouraged to identify opportunities, validate technology ideas and develop scalable products, services and digital ventures.",
        tag: "Venture creation",
        icon: "enterprise",
    },
    {
        number: "05",
        title: "Research progression",
        description:
            "Developed with the analytical foundation required for advanced study, academic research and specialised technology domains.",
        tag: "Higher education",
        icon: "research",
    },
    {
        number: "06",
        title: "Responsible leadership",
        description:
            "Prepared to consider ethics, privacy, security, accessibility and the social impact of technology while making decisions.",
        tag: "Digital responsibility",
        icon: "shield",
    },
];

const outcomeCapabilities = [
    { label: "Technical knowledge", value: "94%" },
    { label: "Problem solving", value: "89%" },
    { label: "Project experience", value: "92%" },
    { label: "Professional readiness", value: "87%" },
];

function TechnologyIcon({ type, className = "h-6 w-6" }) {
    const commonProps = {
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: 1.6,
        strokeLinecap: "round",
        strokeLinejoin: "round",
        className,
        "aria-hidden": true,
    };

    const icons = {
        code: (
            <>
                <path d="m8 9-3 3 3 3M16 9l3 3-3 3M14 5l-4 14" />
            </>
        ),
        lab: (
            <>
                <path d="M9 3h6M10 3v6l-5 9a2 2 0 0 0 1.8 3h10.4a2 2 0 0 0 1.8-3l-5-9V3" />
                <path d="M8 15h8" />
            </>
        ),
        project: (
            <>
                <rect x="3" y="4" width="18" height="16" rx="3" />
                <path d="M3 9h18M8 14h3M8 17h7" />
            </>
        ),
        network: (
            <>
                <circle cx="12" cy="5" r="2.5" />
                <circle cx="5" cy="18" r="2.5" />
                <circle cx="19" cy="18" r="2.5" />
                <path d="m10.5 7-4 8.5M13.5 7l4 8.5M7.5 18h9" />
            </>
        ),
        innovation: (
            <>
                <path d="M9 18h6M10 22h4" />
                <path d="M8.5 15.5A7 7 0 1 1 15.5 15.5c-.9.7-1.5 1.5-1.5 2.5h-4c0-1-.6-1.8-1.5-2.5Z" />
                <path d="M12 3v2M4.2 7.5l1.7 1M19.8 7.5l-1.7 1" />
            </>
        ),
        launch: (
            <>
                <path d="M14 4c3-1 5-1 6-1 0 1 0 3-1 6l-7 7-4-4 6-8Z" />
                <path d="m13 15-1 5-3-3-5 1 3-5M15.5 7.5h.01" />
            </>
        ),
        career: (
            <>
                <rect x="3" y="7" width="18" height="13" rx="2" />
                <path d="M9 7V4h6v3M3 12h18M10 12v2h4v-2" />
            </>
        ),
        industry: (
            <>
                <path d="M3 21V9l6 3V9l6 3V5h6v16H3Z" />
                <path d="M7 16h1M12 16h1M17 16h1" />
            </>
        ),
        enterprise: (
            <>
                <path d="M4 20h16M6 20V8l6-4 6 4v12" />
                <path d="M9 11h6M9 15h6" />
            </>
        ),
        research: (
            <>
                <circle cx="10" cy="10" r="6" />
                <path d="m14.5 14.5 5 5M8 10h4M10 8v4" />
            </>
        ),
        shield: (
            <>
                <path d="M12 3 5 6v5c0 4.5 2.7 8.5 7 10 4.3-1.5 7-5.5 7-10V6l-7-3Z" />
                <path d="m9 12 2 2 4-5" />
            </>
        ),
    };

    return <svg {...commonProps}>{icons[type] || icons.code}</svg>;
}

const placementStats = [
    {
        value: 6500,
        suffix: "+",
        label: "Placement Success Stories",
        note: "Across the university ecosystem",
    },
    {
        value: 45,
        suffix: " LPA",
        label: "Highest Package",
        note: "Exceptional career opportunity",
    },
    {
        value: 250,
        suffix: "+",
        label: "Hiring Partners",
        note: "Across diverse industries",
    },
    {
        value: 150,
        suffix: "+",
        label: "Campus Recruiters",
        note: "Industry engagement network",
    },
];

const careerServices = [
    {
        number: "01",
        title: "Career mentoring",
        text: "Individual guidance for career planning, portfolio development, interviews and professional positioning.",
    },
    {
        number: "02",
        title: "Internship opportunities",
        text: "Industry exposure through internships, live projects, technical assignments and collaborative problem-solving.",
    },
    {
        number: "03",
        title: "Technology certifications",
        text: "Support for industry-relevant certifications across cloud, data, cybersecurity, programming and emerging technologies.",
    },
    {
        number: "04",
        title: "Startup support",
        text: "An enabling environment for product ideation, business validation, prototyping and technology-led entrepreneurship.",
    },
];

const admissionSteps = [
    {
        number: "01",
        title: "Online Application",
        label: "Begin your journey",
        description:
            "Complete the digital application form with your personal, academic and programme information.",
        items: ["Select programme", "Enter academic details", "Submit application"],
    },
    {
        number: "02",
        title: "Document Verification",
        label: "Secure document review",
        description:
            "Upload the required academic and identification documents through the admission interface.",
        items: ["Academic records", "Identity documents", "Recent photograph"],
    },
    {
        number: "03",
        title: "Eligibility Review",
        label: "Academic assessment",
        description:
            "The admissions team evaluates your qualifications according to the eligibility requirements of the selected programme.",
        items: ["Qualification review", "Programme eligibility", "Admission guidance"],
    },
    {
        number: "04",
        title: "Admission Confirmation",
        label: "Receive your offer",
        description:
            "Eligible applicants receive admission confirmation along with programme and enrolment information.",
        items: ["Admission decision", "Offer confirmation", "Enrolment details"],
    },
    {
        number: "05",
        title: "Fee Payment",
        label: "Confirm your seat",
        description:
            "Complete the prescribed fee payment securely using the available university payment channels.",
        items: ["Fee information", "Secure payment", "Payment acknowledgement"],
    },
    {
        number: "06",
        title: "Student Onboarding",
        label: "Enter the ISBM community",
        description:
            "Access orientation information, academic support systems and the resources needed to begin your programme.",
        items: ["Student orientation", "Academic access", "Campus introduction"],
    },
];

const eligibilityCards = [
    {
        title: "Undergraduate programmes",
        code: "UG",
        eligibility: "10+2 or equivalent qualification",
        note: "Programme-specific eligibility conditions may apply.",
    },
    {
        title: "Postgraduate programmes",
        code: "PG",
        eligibility: "Bachelor’s degree in a relevant or accepted discipline",
        note: "Requirements vary according to the selected programme.",
    },
    {
        title: "Diploma programmes",
        code: "DP",
        eligibility: "10th or 10+2 depending on the programme",
        note: "Refer to the programme page for complete requirements.",
    },
    {
        title: "Doctoral programmes",
        code: "PhD",
        eligibility: "Relevant postgraduate qualification",
        note: "Subject to university research admission requirements.",
    },
];

const successStories = [
    {
        name: "Aarav Sharma",
        programme: "B.Tech Computer Science",
        role: "Software Development Intern",
        company: "Technology Enterprise",
        category: "Software Engineering",
        quote:
            "Project-based learning helped me understand how software is planned, developed, tested and improved within a professional environment.",
        image: "/images/student-success-1.webp",
        achievement: "Full-stack project portfolio",
        type: "Internship experience",
    },
    {
        name: "Ananya Verma",
        programme: "B.Sc Artificial Intelligence",
        role: "AI & Machine Learning Learner",
        company: "Innovation Laboratory",
        category: "Artificial Intelligence",
        quote:
            "The combination of coding practice, faculty mentoring and AI experiments gave me the confidence to investigate real datasets.",
        image: "/images/student-success-2.webp",
        achievement: "AI prototype development",
        type: "Innovation journey",
    },
    {
        name: "Rohan Patel",
        programme: "M.Sc Data Science",
        role: "Data Analytics Intern",
        company: "Analytics Organisation",
        category: "Data Science",
        quote:
            "Working with data visualisation and analytical projects enabled me to connect statistical concepts with practical decisions.",
        image: "/images/student-success-3.webp",
        achievement: "Applied analytics project",
        type: "Industry exposure",
    },
    {
        name: "Meera Nair",
        programme: "B.Sc Cybersecurity",
        role: "Cybersecurity Aspirant",
        company: "Digital Security Lab",
        category: "Cybersecurity",
        quote:
            "Security simulations and guided laboratory exercises developed my understanding of digital risks and responsible protection.",
        image: "/images/student-success-4.webp",
        achievement: "Security assessment project",
        type: "Laboratory experience",
    },
];

const faqItems = [
    {
        category: "Admissions",
        question: "What is the eligibility for Information Technology programmes?",
        answer:
            "Eligibility depends on the programme level. Undergraduate programmes generally require 10+2 or an equivalent qualification, while postgraduate programmes require an appropriate bachelor’s degree. Programme-specific requirements should be verified on the respective programme page.",
    },
    {
        category: "Admissions",
        question: "How can I apply for admission?",
        answer:
            "You can complete the online application, upload the required documents and submit your details for eligibility review. Eligible applicants receive admission confirmation and instructions for fee payment and onboarding.",
    },
    {
        category: "Financial support",
        question: "Are scholarships available?",
        answer:
            "Scholarship and financial assistance opportunities may be available according to university policy, academic merit and applicable eligibility conditions. Applicants should contact the admissions team for current information.",
    },
    {
        category: "Careers",
        question: "Does the school provide placement assistance?",
        answer:
            "Students receive career support through skill development, placement preparation, resume guidance, interview practice, industry interaction and access to relevant recruitment opportunities.",
    },
    {
        category: "Industry exposure",
        question: "Are internships included in the learning experience?",
        answer:
            "Internships, live assignments, industry projects and professional exposure are encouraged to help students understand workplace practices and apply technical knowledge.",
    },
    {
        category: "Research",
        question: "Can students participate in research and innovation?",
        answer:
            "Students can explore faculty-guided projects and emerging areas such as Artificial Intelligence, Machine Learning, Data Science, cybersecurity, cloud computing and intelligent systems.",
    },
    {
        category: "Campus life",
        question: "Are hostel facilities available?",
        answer:
            "Hostel availability, accommodation options and applicable charges can be confirmed with the university admissions or campus administration team.",
    },
    {
        category: "Certifications",
        question: "Does the curriculum support industry certifications?",
        answer:
            "The learning environment supports awareness and preparation for relevant certifications in programming, cloud technologies, cybersecurity, data analytics and related technology domains.",
    },
];


/* =========================================================
   SMALL REUSABLE COMPONENTS
========================================================= */

function AnimatedCounter({ value, suffix }) {
    const [count, setCount] = useState(0);
    const counterRef = useRef(null);

    useEffect(() => {
        const element = counterRef.current;
        if (!element) return undefined;

        let animationFrame;
        let hasAnimated = false;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting || hasAnimated) return;

                hasAnimated = true;
                const duration = 1600;
                const startTime = performance.now();

                const animate = (currentTime) => {
                    const progress = Math.min((currentTime - startTime) / duration, 1);
                    const eased = 1 - Math.pow(1 - progress, 3);

                    setCount(Math.floor(value * eased));

                    if (progress < 1) {
                        animationFrame = requestAnimationFrame(animate);
                    }
                };

                animationFrame = requestAnimationFrame(animate);
                observer.disconnect();
            },
            { threshold: 0.35 }
        );

        observer.observe(element);

        return () => {
            observer.disconnect();
            cancelAnimationFrame(animationFrame);
        };
    }, [value]);

    return (
        <span ref={counterRef}>
            {count}
            {suffix}
        </span>
    );
}

function SectionEyebrow({ children, light = false }) {
    return (
        <div className="flex items-center gap-4">
            <span
                className={`h-px w-10 ${light ? "bg-[#D5AD57]" : "bg-[#8B1E3F]"
                    }`}
            />
            <span
                className={`text-xs font-bold uppercase tracking-[0.22em] ${light ? "text-[#D5AD57]" : "text-[#8B1E3F]"
                    }`}
            >
                {children}
            </span>
        </div>
    );
}

function ArrowIcon({ className = "h-4 w-4" }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            className={className}
            aria-hidden="true"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 12h14m-5-5 5 5-5 5"
            />
        </svg>
    );
}

function SliderArrow({ direction, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={`${direction === "previous" ? "Previous" : "Next"} story`}
            className="flex h-12 w-12 items-center justify-center rounded-full border border-[#071A2F]/10 bg-white text-[#071A2F] shadow-lg transition duration-300 hover:border-[#8B1E3F] hover:bg-[#8B1E3F] hover:text-white"
        >
            <ArrowIcon
                className={`h-4 w-4 ${direction === "previous" ? "rotate-180" : ""
                    }`}
            />
        </button>
    );
}

function IT() {
    const itSchool = schools.find(
        (school) => school.id === "school-of-information-and-technology"
    );

    const programmes = itSchool?.courses || [];

    const programmeLevels = [
        "All",
        "Undergraduate",
        "Postgraduate",
        "Post Graduate Diploma",
        "Diploma",
        "Doctorate",
    ];

    const [activeProgrammeLevel, setActiveProgrammeLevel] = useState("All");
    const [programmeSearch, setProgrammeSearch] = useState("");
    const [visibleProgrammes, setVisibleProgrammes] = useState(6);

    const cleanDescription = (description = "") =>
        description
            .replace(/<[^>]*>/g, " ")
            .replace(/&nbsp;/g, " ")
            .replace(/&amp;/g, "&")
            .replace(/\s+/g, " ")
            .trim();

    const filteredProgrammes = programmes.filter((programme) => {
        const matchesLevel =
            activeProgrammeLevel === "All" ||
            programme.level === activeProgrammeLevel;

        const searchValue = programmeSearch.trim().toLowerCase();

        const matchesSearch =
            !searchValue ||
            programme.title.toLowerCase().includes(searchValue) ||
            programme.level.toLowerCase().includes(searchValue) ||
            programme.eligibility.toLowerCase().includes(searchValue);

        return matchesLevel && matchesSearch;
    });

    const handleProgrammeLevel = (level) => {
        setActiveProgrammeLevel(level);
        setVisibleProgrammes(6);
    };

    const [activeLearningStage, setActiveLearningStage] = useState(0);

    const currentLearningStage = learningStages[activeLearningStage];

    const [activeAdmissionStep, setActiveAdmissionStep] = useState(0);
    const [activeFaq, setActiveFaq] = useState(0);

    const testimonialSettings = {
        dots: false,
        arrows: true,
        infinite: successStories.length > 2,
        speed: 650,
        slidesToShow: 2,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 5500,
        pauseOnHover: true,
        nextArrow: <SliderArrow direction="next" />,
        prevArrow: <SliderArrow direction="previous" />,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 1,
                },
            },
        ],
    };

    return (
        <>
            <Header />
            <main>
                <section
                    className="relative isolate min-h-screen overflow-hidden bg-[#050F1F] text-white"
                    aria-labelledby="it-future-heading"
                >
                    {/* Layered institutional background */}
                    <div className="pointer-events-none absolute inset-0 -z-20">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_76%_35%,rgba(67,112,255,0.20),transparent_28%),radial-gradient(circle_at_59%_70%,rgba(139,30,63,0.24),transparent_31%),linear-gradient(120deg,#050F1F_0%,#071A2F_52%,#0C2744_100%)]" />
                        <div className="absolute inset-0 opacity-20 bg-[linear-gradient(rgba(255,255,255,0.055)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.055)_1px,transparent_1px)] bg-[size:64px_64px]" />
                        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D5AD57]/70 to-transparent" />
                    </div>

                    {/* Neural network spread across the hero */}
                    <svg
                        className="pointer-events-none absolute inset-0 -z-10 h-full w-full opacity-40"
                        viewBox="0 0 1600 900"
                        preserveAspectRatio="none"
                        aria-hidden="true"
                    >
                        <defs>
                            <linearGradient id="networkLine" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0" stopColor="#D5AD57" stopOpacity="0" />
                                <stop offset=".45" stopColor="#5D85FF" stopOpacity=".42" />
                                <stop offset="1" stopColor="#8B1E3F" stopOpacity=".18" />
                            </linearGradient>
                        </defs>
                        <g fill="none" stroke="url(#networkLine)" strokeWidth="1">
                            <path d="M130 166 430 75l250 132 312-115 291 145" />
                            <path d="m110 430 350 90 258-145 292 168 430-178" />
                            <path d="m270 760 265-104 301 122 295-142 290 110" />
                            <path d="m430 75 30 445 75 136M680 207l38 168 118 403M992 92l18 451 121 93M1283 237l-152 399" />
                        </g>
                    </svg>

                    {networkNodes.map(([left, top], index) => (
                        <span
                            key={`${left}-${top}`}
                            className={[
                                "pointer-events-none absolute -z-10 h-1.5 w-1.5 rounded-full",
                                index % 3 === 0
                                    ? "bg-[#D5AD57] shadow-[0_0_18px_#D5AD57]"
                                    : "bg-blue-400 shadow-[0_0_16px_#60A5FA]",
                            ].join(" ")}
                            style={{ left, top }}
                            aria-hidden="true"
                        />
                    ))}

                    {/* Vertical research label */}
                    <aside className="absolute inset-y-0 left-0 z-20 hidden w-14 border-r border-white/10 xl:flex xl:flex-col xl:items-center xl:justify-between xl:py-9">
                        <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#D5AD57] [writing-mode:vertical-rl]">
                            School of Information Technology
                        </span>
                        <span className="h-20 w-px bg-gradient-to-b from-[#D5AD57] to-transparent" />
                        <span className="font-mono text-[9px] text-white/35 [writing-mode:vertical-rl]">
                            ISBM · FUTURE SYSTEMS
                        </span>
                    </aside>

                    <div className="mx-auto grid min-h-screen max-w-[1720px] grid-cols-1 lg:grid-cols-[52%_48%] xl:pl-14">
                        {/* Content */}
                        <div className="relative z-20 flex items-center px-5 pb-16 pt-24 sm:px-8 md:px-12 lg:px-14 lg:py-24 xl:px-20">
                            <div className="w-full max-w-[820px]">
                                <div className="mb-9 flex flex-wrap items-center gap-4">
                                    <span className="inline-flex items-center gap-3 border border-[#D5AD57]/30 bg-white/[0.055] px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#D5AD57] backdrop-blur-xl">
                                        <span className="relative flex h-2 w-2">
                                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#D5AD57] opacity-60" />
                                            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#D5AD57]" />
                                        </span>
                                        School of Information &amp; Technology
                                    </span>
                                    <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-white/35">
                                        Research Node / 01
                                    </span>
                                </div>

                                <p className="mb-5 flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.26em] text-blue-300/80">
                                    <span className="h-px w-12 bg-blue-400/70" />
                                    Computing · Intelligence · Impact
                                </p>

                                <h1
                                    id="it-future-heading"
                                    className="max-w-[780px] font-serif text-[clamp(3.5rem,6vw,7rem)] font-normal leading-[0.88] tracking-[-0.055em]"
                                >
                                    Invent the systems{" "}
                                    <span className="relative mt-2 inline-block text-transparent [-webkit-text-stroke:1px_rgba(213,173,87,0.9)]">
                                        shaping tomorrow.
                                        <span
                                            className="absolute -bottom-3 left-1 h-px w-2/3 bg-gradient-to-r from-[#D5AD57] via-blue-400 to-transparent"
                                            aria-hidden="true"
                                        />
                                    </span>
                                </h1>

                                <p className="mt-10 max-w-[670px] border-l border-[#D5AD57]/55 pl-5 text-[15px] leading-7 text-slate-300 sm:text-base sm:leading-8">
                                    An advanced academic environment for artificial intelligence,
                                    machine learning, data science, software engineering, cloud
                                    computing and secure digital systems—where research becomes
                                    technology with real-world impact.
                                </p>

                                <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                                    <a
                                        href="#programmes"
                                        className="group inline-flex min-h-14 items-center justify-between gap-10 bg-[#D5AD57] px-6 text-sm font-bold text-[#071A2F] shadow-[0_18px_45px_rgba(213,173,87,0.18)] transition duration-300 hover:-translate-y-1 hover:bg-[#E1C173] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#071A2F]"
                                    >
                                        Explore Programmes
                                        <span className="transition-transform duration-300 group-hover:translate-x-1">
                                            <Arrow />
                                        </span>
                                    </a>

                                    <a
                                        href="/apply-online"
                                        className="group inline-flex min-h-14 items-center justify-between gap-10 border border-white/20 bg-white/[0.055] px-6 text-sm font-semibold text-white backdrop-blur-lg transition duration-300 hover:-translate-y-1 hover:border-[#D5AD57]/60 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-[#D5AD57]"
                                    >
                                        Begin Your Application
                                        <span className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5">
                                            <Arrow diagonal />
                                        </span>
                                    </a>
                                </div>

                                {/* Programme focus strip */}
                                <div className="mt-12 grid grid-cols-2 border-y border-white/10 xl:grid-cols-4">
                                    {focusAreas.map((area, index) => (
                                        <a
                                            href="#programmes"
                                            key={area.title}
                                            className={[
                                                "group relative min-h-28 overflow-hidden border-white/10 px-4 py-5 transition duration-300 hover:bg-white/[0.07]",
                                                index < 3 ? "xl:border-r" : "",
                                                index % 2 === 0 ? "border-r xl:border-r" : "",
                                                index < 2 ? "border-b xl:border-b-0" : "",
                                            ].join(" ")}
                                        >
                                            <span className="absolute right-3 top-3 font-mono text-[8px] text-white/25">
                                                {area.number}
                                            </span>
                                            <span className="mb-4 block text-[#D5AD57] transition duration-300 group-hover:text-blue-300">
                                                {area.icon}
                                            </span>
                                            <strong className="block max-w-[120px] text-[11px] font-semibold leading-4 text-white/75 transition group-hover:text-white">
                                                {area.title}
                                            </strong>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Cinematic technology visual */}
                        <div className="relative min-h-[720px] overflow-hidden lg:min-h-screen">
                            <img
                                src={heroImage}
                                alt="Advanced AI humanoid robot in a university information technology research laboratory"
                                className="absolute inset-0 h-full w-full scale-[1.03] object-cover object-center saturate-[0.88]"
                            />

                            {/* Blend image into the hero */}
                            <div className="absolute inset-0 bg-gradient-to-r from-[#050F1F] via-[#071A2F]/30 to-transparent" />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#050F1F] via-transparent to-[#050F1F]/35" />
                            <div className="absolute inset-0 bg-blue-950/10 mix-blend-color" />

                            {/* Research interface frame */}
                            <div className="absolute inset-5 border border-white/10 sm:inset-8 lg:inset-x-10 lg:inset-y-12">
                                <span className="absolute -left-px -top-px h-8 w-8 border-l border-t border-[#D5AD57]" />
                                <span className="absolute -right-px -top-px h-8 w-8 border-r border-t border-[#D5AD57]" />
                                <span className="absolute -bottom-px -left-px h-8 w-8 border-b border-l border-[#D5AD57]" />
                                <span className="absolute -bottom-px -right-px h-8 w-8 border-b border-r border-[#D5AD57]" />
                            </div>

                            {/* Holographic AI model panel */}
                            <div className="absolute right-5 top-8 w-[min(72%,320px)] border border-blue-300/20 bg-[#071A2F]/55 p-4 shadow-[0_25px_70px_rgba(0,0,0,0.30)] backdrop-blur-xl sm:right-10 sm:top-14">
                                <div className="mb-4 flex items-center justify-between border-b border-white/10 pb-3">
                                    <span className="font-mono text-[8px] uppercase tracking-[0.18em] text-blue-200/70">
                                        Neural Intelligence Model
                                    </span>
                                    <span className="flex gap-1">
                                        <i className="h-1 w-1 rounded-full bg-[#D5AD57]" />
                                        <i className="h-1 w-1 rounded-full bg-blue-400" />
                                        <i className="h-1 w-1 rounded-full bg-[#8B1E3F]" />
                                    </span>
                                </div>
                                <svg viewBox="0 0 280 120" className="h-28 w-full" aria-hidden="true">
                                    <g stroke="rgba(147,197,253,.35)" strokeWidth="1">
                                        <path d="M20 27 88 14l49 40 59-29 62 38M20 27l46 70 71-43 53 51 68-42M88 14 66 97M137 54l53 51M196 25l-6 80" />
                                    </g>
                                    {[
                                        [20, 27], [88, 14], [137, 54], [196, 25], [258, 63],
                                        [66, 97], [190, 105],
                                    ].map(([cx, cy], index) => (
                                        <circle
                                            key={`${cx}-${cy}`}
                                            cx={cx}
                                            cy={cy}
                                            r={index === 2 ? 6 : 4}
                                            fill={index === 2 ? "#D5AD57" : "#60A5FA"}
                                        />
                                    ))}
                                </svg>
                                <div className="grid grid-cols-3 gap-2 border-t border-white/10 pt-3">
                                    {[
                                        ["98.4%", "Accuracy"],
                                        ["4.2ms", "Latency"],
                                        ["24/7", "Compute"],
                                    ].map(([value, label]) => (
                                        <span key={label}>
                                            <strong className="block font-mono text-xs text-white">{value}</strong>
                                            <small className="text-[7px] uppercase tracking-wider text-white/35">{label}</small>
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Data research panel */}
                            <div className="absolute bottom-28 left-5 w-[min(68%,280px)] border border-white/15 bg-[#071A2F]/60 p-4 backdrop-blur-xl sm:bottom-32 sm:left-10">
                                <div className="mb-4 flex items-center justify-between">
                                    <span className="font-mono text-[8px] uppercase tracking-[0.16em] text-[#D5AD57]">
                                        Data Research / Live
                                    </span>
                                    <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400 shadow-[0_0_12px_#34D399]" />
                                </div>
                                <div className="flex h-20 items-end gap-2 border-b border-white/10">
                                    {[35, 56, 42, 74, 62, 91, 78, 100].map((height, index) => (
                                        <span
                                            key={`${height}-${index}`}
                                            className="flex-1 bg-gradient-to-t from-[#8B1E3F] via-blue-500 to-[#D5AD57]"
                                            style={{ height: `${height}%`, opacity: 0.45 + index * 0.06 }}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Bottom institutional statement */}
                            <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-[#050F1F]/80 px-5 py-5 backdrop-blur-xl sm:px-10">
                                <div className="flex items-center justify-between gap-5">
                                    <div>
                                        <span className="block text-[8px] font-bold uppercase tracking-[0.2em] text-[#D5AD57]">
                                            Advanced Computing &amp; Research
                                        </span>
                                        <strong className="mt-1 block font-serif text-lg font-normal text-white/90">
                                            Future-ready by design.
                                        </strong>
                                    </div>
                                    <a
                                        href="#research"
                                        className="grid h-11 w-11 shrink-0 place-items-center border border-white/20 text-white transition duration-300 hover:border-[#D5AD57] hover:bg-[#D5AD57] hover:text-[#071A2F] focus:outline-none focus:ring-2 focus:ring-[#D5AD57]"
                                        aria-label="Explore technology research"
                                    >
                                        <Arrow diagonal />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Programme */}
                <section
                    id="academic-programmes"
                    className="relative overflow-hidden bg-[#F7F2E8] py-20 sm:py-24 lg:py-32"
                >
                    {/* Background decorations */}
                    <div className="pointer-events-none absolute -left-32 top-40 h-80 w-80 rounded-full bg-[#D5AD57]/10 blur-3xl" />
                    <div className="pointer-events-none absolute -right-32 bottom-20 h-96 w-96 rounded-full bg-[#8B1E3F]/10 blur-3xl" />

                    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        {/* Section heading */}
                        <div className="grid items-end gap-10 lg:grid-cols-12">
                            <div className="lg:col-span-8">
                                <div className="mb-5 flex items-center gap-3">
                                    <span className="h-px w-10 bg-[#D5AD57]" />

                                    <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#8B1E3F]">
                                        Academic pathways
                                    </span>
                                </div>

                                <h2 className="max-w-4xl text-4xl font-semibold leading-[1.08] tracking-[-0.035em] text-[#071A2F] sm:text-5xl lg:text-6xl">
                                    Programmes designed for the{" "}
                                    <span className="font-serif font-normal italic text-[#8B1E3F]">
                                        digital future.
                                    </span>
                                </h2>

                                <p className="mt-6 max-w-2xl text-base leading-8 text-[#132337]/70 sm:text-lg">
                                    Explore future-focused programmes in information technology,
                                    computer science, artificial intelligence, data science, cloud
                                    computing and modern software development.
                                </p>
                            </div>

                            {/* Programme summary */}
                            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-[#071A2F]/10 bg-[#071A2F]/10 sm:grid-cols-3 lg:col-span-4">
                                <div className="bg-white/80 px-4 py-5">
                                    <strong className="block text-2xl text-[#071A2F]">
                                        {programmes.length}
                                    </strong>

                                    <span className="text-xs uppercase tracking-wider text-[#132337]/55">
                                        Programmes
                                    </span>
                                </div>

                                <div className="bg-white/80 px-4 py-5">
                                    <strong className="block text-2xl text-[#071A2F]">
                                        {programmeLevels.length - 1}
                                    </strong>

                                    <span className="text-xs uppercase tracking-wider text-[#132337]/55">
                                        Study levels
                                    </span>
                                </div>

                                <div className="col-span-2 bg-white/80 px-4 py-5 sm:col-span-1">
                                    <strong className="block text-2xl text-[#071A2F]">Future</strong>

                                    <span className="text-xs uppercase tracking-wider text-[#132337]/55">
                                        Focused
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Filters */}
                        <div className="mt-12 rounded-[28px] border border-[#071A2F]/10 bg-white/80 p-2 shadow-[0_20px_70px_rgba(7,26,47,0.08)] backdrop-blur-xl lg:mt-16">
                            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                                {/* Level tabs */}
                                <div className="flex gap-2 overflow-x-auto p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                    {programmeLevels.map((level) => {
                                        const count =
                                            level === "All"
                                                ? programmes.length
                                                : programmes.filter(
                                                    (programme) => programme.level === level
                                                ).length;

                                        const isActive = activeProgrammeLevel === level;

                                        return (
                                            <button
                                                key={level}
                                                type="button"
                                                onClick={() => handleProgrammeLevel(level)}
                                                className={`flex shrink-0 items-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold transition-all duration-300 ${isActive
                                                    ? "bg-[#071A2F] text-white shadow-lg shadow-[#071A2F]/20"
                                                    : "text-[#132337]/65 hover:bg-[#071A2F]/5 hover:text-[#071A2F]"
                                                    }`}
                                            >
                                                {level === "Post Graduate Diploma" ? "PG Diploma" : level}

                                                <span
                                                    className={`rounded-full px-2 py-0.5 text-[10px] ${isActive
                                                        ? "bg-[#D5AD57] text-[#071A2F]"
                                                        : "bg-[#071A2F]/5 text-[#132337]/50"
                                                        }`}
                                                >
                                                    {count}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Search */}
                                <label className="relative m-1 block xl:w-72">
                                    <span className="sr-only">Search programmes</span>

                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#132337]/40"
                                    >
                                        <circle cx="11" cy="11" r="7" />
                                        <path strokeLinecap="round" d="m20 20-4-4" />
                                    </svg>

                                    <input
                                        type="search"
                                        value={programmeSearch}
                                        onChange={(event) => {
                                            setProgrammeSearch(event.target.value);
                                            setVisibleProgrammes(6);
                                        }}
                                        placeholder="Search a programme"
                                        className="h-12 w-full rounded-2xl border border-[#071A2F]/10 bg-white pl-12 pr-4 text-sm text-[#071A2F] outline-none transition placeholder:text-[#132337]/40 focus:border-[#D5AD57] focus:ring-4 focus:ring-[#D5AD57]/15"
                                    />
                                </label>
                            </div>
                        </div>

                        {/* Programme cards */}
                        {filteredProgrammes.length > 0 ? (
                            <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                                {filteredProgrammes
                                    .slice(0, visibleProgrammes)
                                    .map((programme, index) => (
                                        <article
                                            key={programme.name}
                                            className="group relative flex min-h-[350px] flex-col overflow-hidden rounded-[28px] border border-[#071A2F]/10 bg-white p-6 transition duration-500 hover:-translate-y-2 hover:border-[#D5AD57] hover:shadow-[0_26px_65px_rgba(7,26,47,0.13)] sm:p-7"
                                        >
                                            <div className="absolute right-5 top-2 select-none text-7xl font-bold text-[#071A2F]/[0.035]">
                                                {String(index + 1).padStart(2, "0")}
                                            </div>

                                            <div className="relative flex items-start justify-between gap-4">
                                                <span className="rounded-xl bg-[#071A2F] px-3 py-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#D5AD57]">
                                                    {programme.level === "Undergraduate"
                                                        ? "UG"
                                                        : programme.level === "Postgraduate"
                                                            ? "PG"
                                                            : programme.level === "Post Graduate Diploma"
                                                                ? "PG Diploma"
                                                                : programme.level}
                                                </span>

                                                <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[#071A2F]/10 text-[#071A2F] transition duration-300 group-hover:border-[#8B1E3F] group-hover:bg-[#8B1E3F] group-hover:text-white">
                                                    <svg
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="1.8"
                                                        className="h-4 w-4 transition duration-300 group-hover:-rotate-45"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M5 12h14m-5-5 5 5-5 5"
                                                        />
                                                    </svg>
                                                </span>
                                            </div>

                                            <h3 className="relative mt-7 text-xl font-semibold leading-snug text-[#071A2F]">
                                                {programme.title}
                                            </h3>

                                            <p className="relative mt-4 line-clamp-3 text-sm leading-6 text-[#132337]/60">
                                                {cleanDescription(programme.banner_description)}
                                            </p>

                                            <div className="relative mt-auto grid grid-cols-2 gap-4 border-t border-[#071A2F]/10 pt-5">
                                                <div>
                                                    <span className="block text-[10px] font-bold uppercase tracking-wider text-[#8B1E3F]">
                                                        Duration
                                                    </span>

                                                    <strong className="mt-1 block text-sm font-medium text-[#071A2F]">
                                                        {programme.duration}
                                                    </strong>
                                                </div>

                                                <div>
                                                    <span className="block text-[10px] font-bold uppercase tracking-wider text-[#8B1E3F]">
                                                        Study mode
                                                    </span>

                                                    <strong className="mt-1 block text-sm font-medium text-[#071A2F]">
                                                        {programme.mode}
                                                    </strong>
                                                </div>
                                            </div>

                                            <a
                                                href={`/programmes/${programme.name}`}
                                                className="absolute inset-0"
                                                aria-label={`View ${programme.title}`}
                                            />
                                        </article>
                                    ))}
                            </div>
                        ) : (
                            <div className="mt-8 rounded-[28px] border border-dashed border-[#071A2F]/20 bg-white/70 px-6 py-16 text-center">
                                <h3 className="text-xl font-semibold text-[#071A2F]">
                                    No matching programmes found
                                </h3>

                                <p className="mt-2 text-sm text-[#132337]/60">
                                    Try another programme name or select a different study level.
                                </p>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setProgrammeSearch("");
                                        handleProgrammeLevel("All");
                                    }}
                                    className="mt-6 rounded-full bg-[#071A2F] px-6 py-3 text-sm font-semibold text-white"
                                >
                                    Reset filters
                                </button>
                            </div>
                        )}

                        {/* Show more */}
                        {visibleProgrammes < filteredProgrammes.length && (
                            <div className="mt-10 text-center">
                                <p className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-[#132337]/45">
                                    Showing {visibleProgrammes} of {filteredProgrammes.length} programmes
                                </p>

                                <button
                                    type="button"
                                    onClick={() =>
                                        setVisibleProgrammes((previousValue) => previousValue + 6)
                                    }
                                    className="inline-flex items-center gap-3 rounded-full border border-[#071A2F]/15 bg-white px-7 py-3.5 text-sm font-bold text-[#071A2F] shadow-sm transition duration-300 hover:border-[#071A2F] hover:bg-[#071A2F] hover:text-white"
                                >
                                    Show more programmes

                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        className="h-4 w-4 rotate-90"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M5 12h14m-5-5 5 5-5 5"
                                        />
                                    </svg>
                                </button>
                            </div>
                        )}

                        {/* Bottom CTA */}
                        <div className="mt-16 flex flex-col items-start justify-between gap-6 border-t border-[#071A2F]/10 pt-8 sm:flex-row sm:items-center">
                            <div>
                                <p className="text-sm font-semibold text-[#071A2F]">
                                    Need help selecting the right programme?
                                </p>

                                <p className="mt-1 text-sm text-[#132337]/55">
                                    Speak with our admissions team for eligibility and application
                                    guidance.
                                </p>
                            </div>

                            <a
                                href="/contact-us"
                                className="inline-flex items-center gap-3 rounded-full bg-[#8B1E3F] px-6 py-3.5 text-sm font-bold text-white transition duration-300 hover:bg-[#711832] hover:shadow-lg hover:shadow-[#8B1E3F]/20"
                            >
                                Talk to an advisor
                                <span aria-hidden="true">→</span>
                            </a>
                        </div>
                    </div>
                </section>

                {/* Why Choose ISBM Information & Technology School */}
                <section className="relative overflow-hidden bg-[#071A2F] py-24 text-white sm:py-28 lg:py-[120px]">
                    {/* Background decoration */}
                    <div
                        className="pointer-events-none absolute inset-0 opacity-40"
                        aria-hidden="true"
                    >
                        <div className="absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full border border-[#D5AD57]/15" />
                        <div className="absolute -right-20 -top-20 h-[360px] w-[360px] rounded-full border border-white/[0.06]" />
                        <div className="absolute -bottom-40 -left-40 h-[440px] w-[440px] rounded-full bg-[#8B1E3F]/20 blur-[100px]" />

                        <svg
                            viewBox="0 0 600 600"
                            className="absolute right-0 top-0 h-full w-1/2 opacity-[0.07]"
                            fill="none"
                        >
                            <path
                                d="M40 100H180V190H300V85H440V220H550"
                                stroke="#D5AD57"
                                strokeWidth="1"
                            />
                            <path
                                d="M80 340H220V280H370V400H540"
                                stroke="#FFFFFF"
                                strokeWidth="1"
                            />
                            <path
                                d="M150 520V430H300V500H470V350"
                                stroke="#D5AD57"
                                strokeWidth="1"
                            />
                            <circle cx="180" cy="190" r="5" fill="#D5AD57" />
                            <circle cx="300" cy="85" r="5" fill="#D5AD57" />
                            <circle cx="370" cy="400" r="5" fill="#FFFFFF" />
                            <circle cx="470" cy="350" r="5" fill="#D5AD57" />
                        </svg>
                    </div>

                    <div className="relative mx-auto w-[calc(100%-32px)] max-w-[1320px] sm:w-[calc(100%-48px)]">
                        {/* Heading */}
                        <div className="grid gap-10 lg:grid-cols-[1.08fr_0.92fr] lg:items-end lg:gap-24">
                            <div>
                                <div className="mb-6 flex items-center gap-3 text-[#D5AD57]">
                                    <span className="h-[2px] w-[38px] bg-current" />

                                    <span className="text-xs font-extrabold uppercase tracking-[0.16em]">
                                        Why study with us
                                    </span>
                                </div>

                                <h2 className="font-['Libre_Caslon_Display',Georgia,serif] text-[42px] font-normal leading-[1.06] tracking-[-0.035em] sm:text-5xl lg:text-[64px] xl:text-[72px]">
                                    Why choose ISBM
                                    <span className="mt-2 block italic text-[#D5AD57]">
                                        Information &amp; Technology School?
                                    </span>
                                </h2>
                            </div>

                            <div className="border-l border-white/15 pl-6 sm:pl-8">
                                <p className="text-[16px] leading-[1.85] text-white/65">
                                    Choose an academic environment that combines computing fundamentals,
                                    advanced technology specialisations and practical learning to help
                                    students develop relevant capabilities for further study,
                                    innovation and professional careers.
                                </p>

                                <a
                                    href="#academic-programmes"
                                    className="group mt-7 inline-flex items-center gap-4 text-xs font-extrabold uppercase tracking-[0.16em] text-[#D5AD57]"
                                >
                                    Explore academic programmes

                                    <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D5AD57]/40 transition duration-300 group-hover:bg-[#D5AD57] group-hover:text-[#071A2F]">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                                            aria-hidden="true"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M5 12h14m-5-5 5 5-5 5"
                                            />
                                        </svg>
                                    </span>
                                </a>
                            </div>
                        </div>

                        {/* Key differentiators */}
                        <div className="mt-16 grid border-l border-t border-white/10 md:grid-cols-2 lg:mt-20 lg:grid-cols-3">
                            {[
                                {
                                    number: "01",
                                    title: "Programmes for every academic stage",
                                    description:
                                        "Choose from diploma, undergraduate, postgraduate, postgraduate diploma and doctoral pathways designed for different academic and professional goals.",
                                    focus: "Diploma to Ph.D.",
                                    icon: (
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M4 19h16M6 17V9l6-4 6 4v8M9 17v-5h6v5"
                                        />
                                    ),
                                },
                                {
                                    number: "02",
                                    title: "Future-focused specialisations",
                                    description:
                                        "Explore Artificial Intelligence, Machine Learning, Data Science, Cloud Computing, IoT, DevOps, Full Stack Development and Business Intelligence.",
                                    focus: "Emerging technologies",
                                    icon: (
                                        <>
                                            <circle cx="12" cy="12" r="3" />
                                            <path
                                                strokeLinecap="round"
                                                d="M12 3v3m0 12v3M3 12h3m12 0h3M5.6 5.6l2.1 2.1m8.6 8.6 2.1 2.1m0-12.8-2.1 2.1m-8.6 8.6-2.1 2.1"
                                            />
                                        </>
                                    ),
                                },
                                {
                                    number: "03",
                                    title: "Strong computing foundations",
                                    description:
                                        "Develop knowledge in programming, databases, software engineering, computer networks, operating systems and information technology fundamentals.",
                                    focus: "Core technical knowledge",
                                    icon: (
                                        <>
                                            <rect x="3" y="4" width="18" height="13" rx="2" />
                                            <path strokeLinecap="round" d="M8 21h8m-4-4v4" />
                                        </>
                                    ),
                                },
                                {
                                    number: "04",
                                    title: "Practical and project-based learning",
                                    description:
                                        "Apply classroom concepts through computer laboratory sessions, coding exercises, software assignments, case-based challenges and collaborative projects.",
                                    focus: "Learn by building",
                                    icon: (
                                        <>
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="m8 9-4 3 4 3m8-6 4 3-4 3"
                                            />
                                            <path strokeLinecap="round" d="m14 5-4 14" />
                                        </>
                                    ),
                                },
                                {
                                    number: "05",
                                    title: "Faculty guidance and research exposure",
                                    description:
                                        "Receive academic mentorship for technical concepts, project development, research interests and interdisciplinary problem-solving.",
                                    focus: "Mentoring and inquiry",
                                    icon: (
                                        <>
                                            <circle cx="12" cy="8" r="3" />
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M5 21c.8-4 3.1-6 7-6s6.2 2 7 6"
                                            />
                                        </>
                                    ),
                                },
                                {
                                    number: "06",
                                    title: "Career-oriented skill development",
                                    description:
                                        "Strengthen technical capability alongside communication, aptitude, teamwork, interview preparation and professional readiness.",
                                    focus: "Technology careers",
                                    icon: (
                                        <>
                                            <rect x="4" y="7" width="16" height="12" rx="2" />
                                            <path strokeLinecap="round" d="M9 7V5h6v2m-11 5h16" />
                                        </>
                                    ),
                                },
                            ].map((item) => (
                                <article
                                    key={item.number}
                                    className="group relative min-h-[340px] overflow-hidden border-b border-r border-white/10 p-7 transition duration-500 hover:bg-white/[0.055] sm:p-8 lg:p-9"
                                >
                                    {/* Hover accent */}
                                    <span className="absolute left-0 top-0 h-0.5 w-0 bg-[#D5AD57] transition-all duration-500 group-hover:w-full" />

                                    <div className="flex items-start justify-between">
                                        <span className="text-[10px] font-extrabold tracking-[0.2em] text-white/30">
                                            {item.number}
                                        </span>

                                        <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 text-[#D5AD57] transition duration-500 group-hover:border-[#D5AD57] group-hover:bg-[#D5AD57] group-hover:text-[#071A2F]">
                                            <svg
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                className="h-5 w-5"
                                                aria-hidden="true"
                                            >
                                                {item.icon}
                                            </svg>
                                        </span>
                                    </div>

                                    <div className="mt-12">
                                        <span className="text-[9px] font-extrabold uppercase tracking-[0.18em] text-[#D5AD57]">
                                            {item.focus}
                                        </span>

                                        <h3 className="mt-4 font-['Libre_Caslon_Display',Georgia,serif] text-[27px] font-normal leading-[1.18] text-white">
                                            {item.title}
                                        </h3>

                                        <p className="mt-4 text-sm leading-7 text-white/55">
                                            {item.description}
                                        </p>
                                    </div>
                                </article>
                            ))}
                        </div>

                        {/* Technology pathway */}
                        <div className="grid overflow-hidden border-x border-b border-white/10 lg:grid-cols-[0.72fr_1.28fr]">
                            <div className="bg-[#8B1E3F] px-7 py-8 sm:px-9">
                                <span className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#E8C97E]">
                                    A complete technology pathway
                                </span>

                                <p className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal leading-snug text-white">
                                    Begin with fundamentals. Progress towards specialisation.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 bg-white/[0.035] sm:grid-cols-4">
                                {[
                                    ["01", "Understand"],
                                    ["02", "Practise"],
                                    ["03", "Specialise"],
                                    ["04", "Progress"],
                                ].map(([number, title]) => (
                                    <div
                                        key={number}
                                        className="border-r border-white/10 px-5 py-7 last:border-r-0 sm:px-7"
                                    >
                                        <span className="text-[9px] font-bold tracking-[0.18em] text-[#D5AD57]">
                                            {number}
                                        </span>

                                        <strong className="mt-2 block text-sm font-semibold text-white">
                                            {title}
                                        </strong>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/*  FROM THE DEAN'S DESK  */}
                <section
                    className="relative overflow-hidden bg-white py-20 sm:py-24 lg:py-32"
                    aria-labelledby="dean-message-heading"
                >
                    {/* Technology circuit background */}
                    <div className="pointer-events-none absolute inset-0 overflow-hidden">
                        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-[#D5AD57]/10 blur-3xl" />
                        <div className="absolute -bottom-28 -left-20 h-96 w-96 rounded-full bg-[#8B1E3F]/5 blur-3xl" />

                        <svg
                            viewBox="0 0 700 500"
                            className="absolute right-0 top-0 h-full w-1/2 text-[#071A2F] opacity-[0.035]"
                            fill="none"
                            aria-hidden="true"
                        >
                            <path
                                d="M80 30v90h120v90h130v100h160v130"
                                stroke="currentColor"
                                strokeWidth="2"
                            />
                            <path
                                d="M240 0v100h150v90h190"
                                stroke="currentColor"
                                strokeWidth="2"
                            />
                            <path
                                d="M350 500V390h110v-80h170"
                                stroke="currentColor"
                                strokeWidth="2"
                            />
                            {[["80", "120"], ["200", "210"], ["330", "310"], ["490", "440"], ["390", "190"], ["460", "310"]].map(
                                ([cx, cy]) => (
                                    <circle
                                        key={`${cx}-${cy}`}
                                        cx={cx}
                                        cy={cy}
                                        r="7"
                                        fill="currentColor"
                                    />
                                )
                            )}
                        </svg>
                    </div>

                    <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                        <div className="grid items-start gap-14 lg:grid-cols-[0.72fr_1.28fr] lg:gap-20">
                            {/* Editorial heading */}
                            <div className="lg:sticky lg:top-28">
                                <div className="flex items-center gap-4">
                                    <span className="h-px w-10 bg-[#8B1E3F]" />
                                    <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#8B1E3F]">
                                        Academic leadership
                                    </span>
                                </div>

                                <h2
                                    id="dean-message-heading"
                                    className="mt-6 text-[clamp(42px,5vw,72px)] font-normal leading-[1.02] tracking-[-0.045em] text-[#071A2F]"
                                    style={{ fontFamily: '"Libre Caslon Display", Georgia, serif' }}
                                >
                                    From the
                                    <span className="block italic text-[#8B1E3F]">Dean&apos;s Desk.</span>
                                </h2>

                                <p className="mt-7 max-w-md text-base leading-8 text-[#132337]/65">
                                    A vision for purposeful technology education, responsible
                                    innovation and intellectual leadership.
                                </p>

                                <div className="mt-10 flex items-center gap-4">
                                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#071A2F] text-[#D5AD57]">
                                        <TechnologyIcon type="network" className="h-5 w-5" />
                                    </span>

                                    <div>
                                        <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#8B1E3F]">
                                            School vision
                                        </span>
                                        <strong className="mt-1 block text-sm font-semibold text-[#071A2F]">
                                            Technology with purpose
                                        </strong>
                                    </div>
                                </div>
                            </div>

                            {/* Message */}
                            <article className="relative overflow-hidden rounded-[32px] border border-[#071A2F]/10 bg-[#F7F2E8] p-7 shadow-[0_30px_90px_rgba(7,26,47,0.10)] sm:p-10 lg:p-14">
                                <div className="absolute right-0 top-0 h-40 w-40 rounded-bl-[100px] bg-[#D5AD57]/20" />

                                <div className="absolute right-8 top-7 select-none text-[110px] font-serif leading-none text-[#8B1E3F]/10">
                                    “
                                </div>

                                <div className="relative">
                                    <span className="inline-flex items-center gap-2 rounded-full border border-[#A77B2D]/20 bg-white/70 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#A77B2D] backdrop-blur">
                                        <span className="h-1.5 w-1.5 rounded-full bg-[#D5AD57]" />
                                        A message to future technologists
                                    </span>

                                    <blockquote
                                        className="mt-9 max-w-3xl text-2xl font-normal leading-[1.45] tracking-[-0.02em] text-[#071A2F] sm:text-3xl lg:text-[38px]"
                                        style={{ fontFamily: '"Libre Caslon Display", Georgia, serif' }}
                                    >
                                        “Technology becomes truly meaningful when knowledge is guided by
                                        curiosity, strengthened by integrity and applied to improve the
                                        world around us.”
                                    </blockquote>

                                    <div className="my-9 h-px w-full bg-gradient-to-r from-[#8B1E3F]/40 via-[#D5AD57]/40 to-transparent" />

                                    <div className="space-y-5 text-[15px] leading-8 text-[#132337]/70 sm:text-base">
                                        <p>
                                            At the School of Information Technology, we prepare students to
                                            participate thoughtfully in a world being reshaped by Artificial
                                            Intelligence, data, cloud platforms, intelligent systems and
                                            connected technologies.
                                        </p>

                                        <p>
                                            Our academic environment brings together strong computing
                                            foundations, practical experimentation and multidisciplinary
                                            problem solving. Students are encouraged to question assumptions,
                                            explore new possibilities and create technology that is secure,
                                            inclusive and responsible.
                                        </p>

                                        <p>
                                            Through faculty mentorship, projects, research exposure and
                                            engagement with industry, learners develop the confidence to move
                                            beyond using technology and begin shaping its future.
                                        </p>
                                    </div>

                                    <div className="mt-10 flex flex-col gap-5 border-t border-[#071A2F]/10 pt-7 sm:flex-row sm:items-end sm:justify-between">
                                        <div>
                                            <span
                                                className="text-2xl italic text-[#071A2F]"
                                                style={{ fontFamily: '"Libre Caslon Display", Georgia, serif' }}
                                            >
                                                Dean, School of Information Technology
                                            </span>
                                            <span className="mt-2 block text-xs font-bold uppercase tracking-[0.16em] text-[#8B1E3F]">
                                                ISBM University
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2" aria-hidden="true">
                                            {[0, 1, 2, 3, 4].map((item) => (
                                                <span
                                                    key={item}
                                                    className={`rounded-full ${item === 2
                                                        ? "h-3 w-3 bg-[#8B1E3F]"
                                                        : "h-2 w-2 bg-[#D5AD57]"
                                                        }`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </article>
                        </div>
                    </div>
                </section>

                {/*  LEARNING METHODOLOGY  */}
                <section
                    className="relative overflow-hidden bg-[#8B1E3F]/[0.055] py-20 sm:py-24 lg:py-32"
                    aria-labelledby="learning-methodology-heading"
                >
                    <div className="pointer-events-none absolute inset-0">
                        <div className="absolute left-[10%] top-20 h-32 w-32 rounded-full border border-[#8B1E3F]/10" />
                        <div className="absolute left-[13%] top-28 h-16 w-16 rounded-full border border-[#D5AD57]/30" />

                        <div
                            className="absolute inset-0 opacity-[0.025]"
                            style={{
                                backgroundImage:
                                    "radial-gradient(#071A2F 1px, transparent 1px)",
                                backgroundSize: "24px 24px",
                            }}
                        />
                    </div>

                    <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                        <div className="grid gap-8 lg:grid-cols-[1fr_0.72fr] lg:items-end">
                            <div>
                                <div className="flex items-center gap-4">
                                    <span className="h-px w-10 bg-[#8B1E3F]" />
                                    <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#8B1E3F]">
                                        Learning methodology
                                    </span>
                                </div>

                                <h2
                                    id="learning-methodology-heading"
                                    className="mt-6 max-w-4xl text-[clamp(42px,5vw,72px)] font-normal leading-[1.03] tracking-[-0.045em] text-[#071A2F]"
                                    style={{ fontFamily: '"Libre Caslon Display", Georgia, serif' }}
                                >
                                    Knowledge moves from
                                    <span className="italic text-[#8B1E3F]"> concept to creation.</span>
                                </h2>
                            </div>

                            <p className="max-w-xl text-base leading-8 text-[#132337]/65 lg:pb-2">
                                Our learning journey combines academic depth with continuous
                                experimentation, industry exposure and real-world application.
                            </p>
                        </div>

                        <div className="mt-14 overflow-hidden rounded-[32px] border border-[#071A2F]/10 bg-white shadow-[0_30px_90px_rgba(7,26,47,0.09)]">
                            {/* Journey navigation */}
                            <div
                                className="overflow-x-auto border-b border-[#071A2F]/10"
                                role="tablist"
                                aria-label="Learning journey stages"
                            >
                                <div className="flex min-w-[760px]">
                                    {learningStages.map((stage, index) => {
                                        const isActive = activeLearningStage === index;

                                        return (
                                            <button
                                                key={stage.number}
                                                type="button"
                                                role="tab"
                                                aria-selected={isActive}
                                                onClick={() => setActiveLearningStage(index)}
                                                className={`group relative flex min-w-[126px] flex-1 flex-col items-start px-5 py-5 text-left transition duration-300 sm:px-6 ${isActive
                                                    ? "bg-[#071A2F] text-white"
                                                    : "bg-white text-[#071A2F] hover:bg-[#F7F2E8]"
                                                    }`}
                                            >
                                                <span
                                                    className={`text-[10px] font-bold uppercase tracking-[0.2em] ${isActive ? "text-[#D5AD57]" : "text-[#8B1E3F]"
                                                        }`}
                                                >
                                                    {stage.number}
                                                </span>

                                                <strong className="mt-2 text-sm font-semibold">
                                                    {stage.label}
                                                </strong>

                                                <span
                                                    className={`absolute bottom-0 left-0 h-[3px] transition-all duration-300 ${isActive
                                                        ? "w-full bg-[#D5AD57]"
                                                        : "w-0 bg-[#8B1E3F] group-hover:w-full"
                                                        }`}
                                                />
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Active journey panel */}
                            <div className="grid min-h-[480px] lg:grid-cols-[0.9fr_1.1fr]">
                                <div className="relative overflow-hidden bg-[#071A2F] p-7 text-white sm:p-10 lg:p-12">
                                    <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full border border-[#D5AD57]/20" />
                                    <div className="absolute -bottom-12 -right-12 h-48 w-48 rounded-full border border-[#D5AD57]/20" />

                                    <svg
                                        viewBox="0 0 400 300"
                                        className="absolute inset-0 h-full w-full opacity-[0.08]"
                                        fill="none"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M0 70h90l35 35h70l35-35h170M0 220h75l40-40h90l50 50h145"
                                            stroke="#D5AD57"
                                            strokeWidth="2"
                                        />
                                        <circle cx="125" cy="105" r="6" fill="#D5AD57" />
                                        <circle cx="205" cy="180" r="6" fill="#D5AD57" />
                                        <circle cx="255" cy="230" r="6" fill="#D5AD57" />
                                    </svg>

                                    <div className="relative flex h-full flex-col">
                                        <div className="flex items-start justify-between">
                                            <span className="flex h-16 w-16 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-[#D5AD57] backdrop-blur">
                                                <TechnologyIcon
                                                    type={currentLearningStage.icon}
                                                    className="h-7 w-7"
                                                />
                                            </span>

                                            <span
                                                className="text-[72px] leading-none text-white/[0.07] sm:text-[100px]"
                                                style={{
                                                    fontFamily: '"Libre Caslon Display", Georgia, serif',
                                                }}
                                            >
                                                {currentLearningStage.number}
                                            </span>
                                        </div>

                                        <div className="mt-auto pt-16">
                                            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#D5AD57]">
                                                {currentLearningStage.short}
                                            </span>

                                            <h3
                                                className="mt-4 max-w-lg text-3xl font-normal leading-tight sm:text-4xl lg:text-5xl"
                                                style={{
                                                    fontFamily: '"Libre Caslon Display", Georgia, serif',
                                                }}
                                            >
                                                {currentLearningStage.title}
                                            </h3>

                                            <p className="mt-6 max-w-lg text-sm leading-7 text-white/65 sm:text-base sm:leading-8">
                                                {currentLearningStage.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col justify-center p-7 sm:p-10 lg:p-12">
                                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B1E3F]">
                                        What the experience includes
                                    </span>

                                    <div className="mt-7 space-y-4">
                                        {currentLearningStage.highlights.map((highlight, index) => (
                                            <div
                                                key={highlight}
                                                className="group flex items-center gap-5 rounded-2xl border border-[#071A2F]/10 bg-[#F7F2E8]/60 p-5 transition duration-300 hover:-translate-y-1 hover:border-[#D5AD57] hover:bg-white hover:shadow-[0_18px_40px_rgba(7,26,47,0.08)]"
                                            >
                                                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#8B1E3F] text-sm font-bold text-white">
                                                    {String(index + 1).padStart(2, "0")}
                                                </span>

                                                <strong className="text-sm font-semibold text-[#071A2F] sm:text-base">
                                                    {highlight}
                                                </strong>

                                                <svg
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.7"
                                                    className="ml-auto h-4 w-4 text-[#A77B2D] transition duration-300 group-hover:translate-x-1"
                                                    aria-hidden="true"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M5 12h14m-5-5 5 5-5 5"
                                                    />
                                                </svg>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-8 flex items-center gap-4">
                                        <div className="h-1 flex-1 overflow-hidden rounded-full bg-[#071A2F]/10">
                                            <div
                                                className="h-full rounded-full bg-gradient-to-r from-[#8B1E3F] to-[#D5AD57] transition-all duration-500"
                                                style={{
                                                    width: `${((activeLearningStage + 1) / learningStages.length) * 100
                                                        }%`,
                                                }}
                                            />
                                        </div>

                                        <span className="text-xs font-bold text-[#071A2F]">
                                            {activeLearningStage + 1}/{learningStages.length}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Supporting experience points */}
                        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            {[
                                ["AI-enabled learning", "Intelligent tools support exploration and experimentation."],
                                ["Live coding practice", "Students build, test and improve working solutions."],
                                ["Research mentorship", "Faculty guidance develops analytical and research ability."],
                                ["Industry engagement", "Professional exposure connects learning with practice."],
                            ].map(([title, description], index) => (
                                <div
                                    key={title}
                                    className="rounded-2xl border border-[#8B1E3F]/10 bg-white/70 p-5 backdrop-blur transition duration-300 hover:bg-white hover:shadow-lg"
                                >
                                    <span className="text-xs font-bold text-[#8B1E3F]">
                                        0{index + 1}
                                    </span>
                                    <h3 className="mt-3 text-base font-semibold text-[#071A2F]">
                                        {title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-6 text-[#132337]/60">
                                        {description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/*  SCHOOL OUTCOMES  */}
                <section
                    className="relative overflow-hidden bg-[#071A2F] py-20 text-white sm:py-24 lg:py-32"
                    aria-labelledby="school-outcomes-heading"
                >
                    {/* Digital background */}
                    <div className="pointer-events-none absolute inset-0">
                        <div className="absolute right-[-10%] top-[-30%] h-[650px] w-[650px] rounded-full bg-[#8B1E3F]/20 blur-[130px]" />
                        <div className="absolute bottom-[-40%] left-[-10%] h-[600px] w-[600px] rounded-full bg-[#D5AD57]/10 blur-[130px]" />

                        <svg
                            viewBox="0 0 1400 700"
                            className="absolute inset-0 h-full w-full opacity-[0.06]"
                            fill="none"
                            aria-hidden="true"
                        >
                            <path
                                d="M0 120h220l90 90h210l90-90h240l90 90h460M0 510h190l100-100h260l120 120h260l80-80h390"
                                stroke="#D5AD57"
                                strokeWidth="2"
                            />
                            <path
                                d="M240 0v130M520 210v130M940 210v140M670 530v170"
                                stroke="#FFFFFF"
                                strokeWidth="1.5"
                            />
                            {[["220", "120"], ["310", "210"], ["520", "210"], ["610", "120"], ["940", "210"], ["290", "410"], ["670", "530"], ["1010", "450"]].map(
                                ([cx, cy]) => (
                                    <circle
                                        key={`${cx}-${cy}`}
                                        cx={cx}
                                        cy={cy}
                                        r="7"
                                        fill="#D5AD57"
                                    />
                                )
                            )}
                        </svg>
                    </div>

                    <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                        <div className="grid gap-8 lg:grid-cols-[1fr_0.7fr] lg:items-end">
                            <div>
                                <div className="flex items-center gap-4">
                                    <span className="h-px w-10 bg-[#D5AD57]" />
                                    <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#D5AD57]">
                                        School outcomes
                                    </span>
                                </div>

                                <h2
                                    id="school-outcomes-heading"
                                    className="mt-6 max-w-4xl text-[clamp(42px,5vw,72px)] font-normal leading-[1.03] tracking-[-0.045em] text-white"
                                    style={{ fontFamily: '"Libre Caslon Display", Georgia, serif' }}
                                >
                                    Education measured by
                                    <span className="italic text-[#D5AD57]"> what graduates become.</span>
                                </h2>
                            </div>

                            <p className="max-w-xl text-base leading-8 text-white/60 lg:pb-2">
                                The outcome is more than a qualification—it is the ability to think
                                critically, create responsibly and contribute confidently within a
                                changing digital world.
                            </p>
                        </div>

                        <div className="mt-14 grid gap-6 xl:grid-cols-[0.72fr_1.28fr]">
                            {/* Capability dashboard */}
                            <div className="relative overflow-hidden rounded-[30px] border border-white/10 bg-white/[0.06] p-7 backdrop-blur-xl sm:p-9">
                                <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full border border-[#D5AD57]/20" />
                                <div className="absolute -right-8 -top-8 h-36 w-36 rounded-full border border-[#D5AD57]/20" />

                                <div className="relative">
                                    <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#D5AD57]">
                                        Graduate capability profile
                                    </span>

                                    <h3
                                        className="mt-5 text-3xl font-normal leading-tight"
                                        style={{ fontFamily: '"Libre Caslon Display", Georgia, serif' }}
                                    >
                                        Future-ready by design.
                                    </h3>

                                    <p className="mt-4 text-sm leading-7 text-white/55">
                                        Every stage of learning contributes to a balanced professional
                                        profile built around knowledge, application and responsibility.
                                    </p>

                                    <div className="mt-9 space-y-6">
                                        {outcomeCapabilities.map((capability, index) => (
                                            <div key={capability.label}>
                                                <div className="mb-2 flex items-center justify-between">
                                                    <span className="text-sm font-medium text-white/80">
                                                        {capability.label}
                                                    </span>
                                                    <span className="text-xs font-bold text-[#D5AD57]">
                                                        0{index + 1}
                                                    </span>
                                                </div>

                                                <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
                                                    <div
                                                        className="h-full rounded-full bg-gradient-to-r from-[#8B1E3F] via-[#D5AD57] to-[#F7F2E8]"
                                                        style={{ width: capability.value }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-10 grid grid-cols-3 gap-3 border-t border-white/10 pt-7">
                                        {[
                                            ["Learn", "Depth"],
                                            ["Build", "Ability"],
                                            ["Lead", "Impact"],
                                        ].map(([action, value]) => (
                                            <div key={action}>
                                                <strong className="block text-sm text-[#D5AD57]">
                                                    {action}
                                                </strong>
                                                <span className="mt-1 block text-xs text-white/45">
                                                    {value}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Outcome cards */}
                            <div className="grid gap-4 sm:grid-cols-2">
                                {schoolOutcomes.map((outcome) => (
                                    <article
                                        key={outcome.number}
                                        className="group relative min-h-[265px] overflow-hidden rounded-[26px] border border-white/10 bg-white/[0.045] p-6 transition duration-500 hover:-translate-y-1 hover:border-[#D5AD57]/60 hover:bg-white/[0.085] sm:p-7"
                                    >
                                        <div className="absolute -right-12 -top-12 h-36 w-36 rounded-full border border-white/10 transition duration-500 group-hover:scale-125 group-hover:border-[#D5AD57]/30" />

                                        <div className="relative flex items-start justify-between">
                                            <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.07] text-[#D5AD57] transition duration-300 group-hover:bg-[#D5AD57] group-hover:text-[#071A2F]">
                                                <TechnologyIcon type={outcome.icon} className="h-5 w-5" />
                                            </span>

                                            <span className="text-xs font-bold text-white/25">
                                                {outcome.number}
                                            </span>
                                        </div>

                                        <div className="relative mt-8">
                                            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#D5AD57]">
                                                {outcome.tag}
                                            </span>

                                            <h3
                                                className="mt-3 text-2xl font-normal text-white"
                                                style={{
                                                    fontFamily: '"Libre Caslon Display", Georgia, serif',
                                                }}
                                            >
                                                {outcome.title}
                                            </h3>

                                            <p className="mt-4 text-sm leading-7 text-white/55">
                                                {outcome.description}
                                            </p>
                                        </div>

                                        <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-gradient-to-r from-[#D5AD57] to-[#8B1E3F] transition-all duration-500 group-hover:w-full" />
                                    </article>
                                ))}
                            </div>
                        </div>

                        {/* Final impact statement */}
                        <div className="mt-8 flex flex-col gap-7 rounded-[28px] border border-[#D5AD57]/20 bg-gradient-to-r from-[#D5AD57]/10 via-white/[0.04] to-[#8B1E3F]/10 p-7 sm:p-9 lg:flex-row lg:items-center lg:justify-between">
                            <div className="max-w-3xl">
                                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D5AD57]">
                                    The graduate promise
                                </span>

                                <p
                                    className="mt-3 text-2xl font-normal leading-snug text-white sm:text-3xl"
                                    style={{ fontFamily: '"Libre Caslon Display", Georgia, serif' }}
                                >
                                    Curious minds. Capable professionals.
                                    <span className="italic text-[#D5AD57]">
                                        {" "}
                                        Responsible technology leaders.
                                    </span>
                                </p>
                            </div>

                            <a
                                href="/admissions"
                                className="inline-flex shrink-0 items-center justify-center gap-3 rounded-full bg-[#D5AD57] px-7 py-4 text-sm font-bold text-[#071A2F] transition duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-[0_18px_45px_rgba(0,0,0,0.25)]"
                            >
                                Begin your journey
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    className="h-4 w-4"
                                    aria-hidden="true"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M5 12h14m-5-5 5 5-5 5"
                                    />
                                </svg>
                            </a>
                        </div>
                    </div>
                </section>

                {/* Faculty */}
                <Faculty />

                {/*  CAREER SERVICES & PLACEMENTS  */}
                <section
                    className="relative overflow-hidden bg-white py-20 sm:py-24 lg:py-32"
                    aria-labelledby="it-careers-heading"
                >
                    <div className="pointer-events-none absolute inset-0">
                        <div className="absolute -right-44 top-20 h-[500px] w-[500px] rounded-full bg-[#D5AD57]/10 blur-[110px]" />

                        <svg
                            viewBox="0 0 700 600"
                            className="absolute right-0 top-0 h-full w-1/2 text-[#071A2F] opacity-[0.035]"
                            fill="none"
                            aria-hidden="true"
                        >
                            <path
                                d="M40 100h140l80 80h160l80-80h160M180 100v-70M420 180v110M500 100v-80M0 430h190l70-70h190l100 100h150"
                                stroke="currentColor"
                                strokeWidth="2"
                            />
                            <circle cx="180" cy="100" r="7" fill="currentColor" />
                            <circle cx="420" cy="180" r="7" fill="currentColor" />
                            <circle cx="260" cy="360" r="7" fill="currentColor" />
                            <circle cx="550" cy="460" r="7" fill="currentColor" />
                        </svg>
                    </div>

                    <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                        <div className="grid gap-8 lg:grid-cols-[1fr_0.65fr] lg:items-end">
                            <div>
                                <SectionEyebrow>Career services & placements</SectionEyebrow>

                                <h2
                                    id="it-careers-heading"
                                    className="mt-6 max-w-4xl text-[clamp(42px,5vw,72px)] font-normal leading-[1.03] tracking-[-0.045em] text-[#071A2F]"
                                    style={{
                                        fontFamily: '"Libre Caslon Display", Georgia, serif',
                                    }}
                                >
                                    From technical ability to
                                    <span className="italic text-[#8B1E3F]">
                                        {" "}
                                        professional impact.
                                    </span>
                                </h2>
                            </div>

                            <p className="max-w-xl text-base leading-8 text-[#132337]/65 lg:pb-2">
                                A connected career ecosystem helps students translate academic
                                learning into portfolios, internships, professional confidence
                                and future-ready technology careers.
                            </p>
                        </div>

                        {/* Placement dashboard */}
                        <div className="mt-14 overflow-hidden rounded-[32px] bg-[#071A2F] shadow-[0_35px_100px_rgba(7,26,47,0.20)]">
                            <div className="grid lg:grid-cols-[0.72fr_1.28fr]">
                                <div className="relative overflow-hidden border-b border-white/10 p-7 text-white sm:p-10 lg:border-b-0 lg:border-r lg:p-12">
                                    <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#8B1E3F]/30 blur-3xl" />
                                    <div className="absolute -bottom-32 -left-24 h-72 w-72 rounded-full bg-[#D5AD57]/15 blur-3xl" />

                                    <div className="relative">
                                        <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.07] px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#D5AD57]">
                                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#D5AD57]" />
                                            Career intelligence
                                        </span>

                                        <h3
                                            className="mt-8 text-4xl font-normal leading-tight sm:text-5xl"
                                            style={{
                                                fontFamily: '"Libre Caslon Display", Georgia, serif',
                                            }}
                                        >
                                            Your career is developed
                                            <span className="block italic text-[#D5AD57]">
                                                from day one.
                                            </span>
                                        </h3>

                                        <p className="mt-6 max-w-md text-sm leading-7 text-white/60 sm:text-base sm:leading-8">
                                            Career preparation is integrated through skill mapping,
                                            mentoring, projects, certifications, internships and
                                            industry engagement.
                                        </p>

                                        <a
                                            href="/placements"
                                            className="mt-9 inline-flex items-center gap-3 rounded-full bg-[#D5AD57] px-7 py-4 text-sm font-bold text-[#071A2F] transition duration-300 hover:-translate-y-1 hover:bg-white"
                                        >
                                            Explore placements
                                            <ArrowIcon />
                                        </a>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2">
                                    {placementStats.map((stat, index) => (
                                        <div
                                            key={stat.label}
                                            className={`group relative min-h-[220px] p-6 transition duration-300 hover:bg-white/[0.06] sm:p-8 ${index % 2 === 0 ? "border-r border-white/10" : ""
                                                } ${index < 2 ? "border-b border-white/10" : ""}`}
                                        >
                                            <span className="absolute right-5 top-4 text-xs font-bold text-white/15">
                                                0{index + 1}
                                            </span>

                                            <strong
                                                className="block text-[clamp(34px,4vw,56px)] font-normal text-[#D5AD57]"
                                                style={{
                                                    fontFamily: '"Libre Caslon Display", Georgia, serif',
                                                }}
                                            >
                                                <AnimatedCounter
                                                    value={stat.value}
                                                    suffix={stat.suffix}
                                                />
                                            </strong>

                                            <h3 className="mt-5 text-sm font-semibold text-white sm:text-base">
                                                {stat.label}
                                            </h3>

                                            <p className="mt-2 text-xs leading-5 text-white/45 sm:text-sm">
                                                {stat.note}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Career development journey */}
                        <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                            {careerServices.map((service) => (
                                <article
                                    key={service.number}
                                    className="group relative overflow-hidden rounded-[24px] border border-[#071A2F]/10 bg-[#F7F2E8]/60 p-6 transition duration-500 hover:-translate-y-2 hover:border-[#D5AD57] hover:bg-white hover:shadow-[0_24px_60px_rgba(7,26,47,0.11)]"
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-bold text-[#8B1E3F]">
                                            {service.number}
                                        </span>
                                        <span className="h-2 w-2 rounded-full bg-[#D5AD57] transition duration-300 group-hover:scale-[2]" />
                                    </div>

                                    <h3
                                        className="mt-8 text-2xl font-normal text-[#071A2F]"
                                        style={{
                                            fontFamily: '"Libre Caslon Display", Georgia, serif',
                                        }}
                                    >
                                        {service.title}
                                    </h3>

                                    <p className="mt-4 text-sm leading-7 text-[#132337]/60">
                                        {service.text}
                                    </p>
                                </article>
                            ))}
                        </div>

                        {/* Recruiters */}
                        <div className="mt-14 border-t border-[#071A2F]/10 pt-9">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                                <div>
                                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B1E3F]">
                                        Technology ecosystem
                                    </span>
                                    <h3 className="mt-2 text-xl font-semibold text-[#071A2F]">
                                        Organisations shaping global technology careers
                                    </h3>
                                </div>

                                <span className="text-xs text-[#132337]/50">
                                    Representative industry ecosystem
                                </span>
                            </div>

                            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
                                {recruiters.map((recruiter) => (
                                    <div
                                        key={recruiter}
                                        className="group flex h-[82px] items-center justify-center rounded-2xl border border-[#071A2F]/10 bg-white px-5 transition duration-300 hover:-translate-y-1 hover:border-[#D5AD57] hover:shadow-lg"
                                    >
                                        <img
                                            src={recruiter}
                                            alt={`${recruiter} logo`}
                                            loading="lazy"
                                            className="max-h-10 w-auto max-w-full object-contain transition duration-300"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/*  ADMISSION PROCESS & ELIGIBILITY  */}
                <section
                    className="relative overflow-hidden bg-[#8B1E3F]/[0.055] py-20 sm:py-24 lg:py-32"
                    aria-labelledby="admission-process-heading"
                >
                    <div
                        className="pointer-events-none absolute inset-0 opacity-[0.025]"
                        style={{
                            backgroundImage:
                                "radial-gradient(#071A2F 1px, transparent 1px)",
                            backgroundSize: "26px 26px",
                        }}
                    />

                    <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                        <div className="max-w-4xl">
                            <SectionEyebrow>Admissions & eligibility</SectionEyebrow>

                            <h2
                                id="admission-process-heading"
                                className="mt-6 text-[clamp(42px,5vw,72px)] font-normal leading-[1.03] tracking-[-0.045em] text-[#071A2F]"
                                style={{
                                    fontFamily: '"Libre Caslon Display", Georgia, serif',
                                }}
                            >
                                A clear digital pathway to
                                <span className="italic text-[#8B1E3F]">
                                    {" "}
                                    your future in technology.
                                </span>
                            </h2>

                            <p className="mt-7 max-w-2xl text-base leading-8 text-[#132337]/65">
                                Move through a simple, guided admission experience—from your
                                first application to becoming part of the ISBM community.
                            </p>
                        </div>

                        <div className="mt-14 overflow-hidden rounded-[32px] border border-[#071A2F]/10 bg-white shadow-[0_30px_90px_rgba(7,26,47,0.10)]">
                            {/* Progress navigation */}
                            <div
                                className="overflow-x-auto border-b border-[#071A2F]/10"
                                role="tablist"
                                aria-label="Admission process"
                            >
                                <div className="flex min-w-[800px]">
                                    {admissionSteps.map((step, index) => {
                                        const isActive = activeAdmissionStep === index;
                                        const isComplete = index < activeAdmissionStep;

                                        return (
                                            <button
                                                key={step.number}
                                                type="button"
                                                role="tab"
                                                aria-selected={isActive}
                                                onClick={() => setActiveAdmissionStep(index)}
                                                className={`relative flex flex-1 flex-col items-start px-5 py-5 text-left transition duration-300 ${isActive
                                                    ? "bg-[#071A2F] text-white"
                                                    : "hover:bg-[#F7F2E8]"
                                                    }`}
                                            >
                                                <span
                                                    className={`flex h-8 w-8 items-center justify-center rounded-full border text-[10px] font-bold ${isActive
                                                        ? "border-[#D5AD57] bg-[#D5AD57] text-[#071A2F]"
                                                        : isComplete
                                                            ? "border-[#8B1E3F] bg-[#8B1E3F] text-white"
                                                            : "border-[#071A2F]/15 text-[#071A2F]"
                                                        }`}
                                                >
                                                    {isComplete ? "✓" : step.number}
                                                </span>

                                                <strong className="mt-3 text-xs font-semibold">
                                                    {step.title}
                                                </strong>

                                                {index < admissionSteps.length - 1 && (
                                                    <span className="absolute right-[-7px] top-9 z-10 hidden h-px w-4 bg-[#D5AD57] sm:block" />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Active admission panel */}
                            <div className="grid lg:grid-cols-[0.75fr_1.25fr]">
                                <div className="relative overflow-hidden bg-[#071A2F] p-8 text-white sm:p-10 lg:p-12">
                                    <span
                                        className="absolute right-5 top-0 select-none text-[130px] leading-none text-white/[0.04]"
                                        style={{
                                            fontFamily: '"Libre Caslon Display", Georgia, serif',
                                        }}
                                    >
                                        {admissionSteps[activeAdmissionStep].number}
                                    </span>

                                    <div className="relative">
                                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D5AD57]">
                                            Current stage
                                        </span>

                                        <h3
                                            className="mt-5 text-4xl font-normal leading-tight"
                                            style={{
                                                fontFamily: '"Libre Caslon Display", Georgia, serif',
                                            }}
                                        >
                                            {admissionSteps[activeAdmissionStep].title}
                                        </h3>

                                        <p className="mt-5 text-sm leading-7 text-white/60">
                                            {admissionSteps[activeAdmissionStep].description}
                                        </p>

                                        <div className="mt-9 h-1.5 overflow-hidden rounded-full bg-white/10">
                                            <div
                                                className="h-full rounded-full bg-gradient-to-r from-[#D5AD57] to-[#8B1E3F] transition-all duration-500"
                                                style={{
                                                    width: `${((activeAdmissionStep + 1) /
                                                        admissionSteps.length) *
                                                        100
                                                        }%`,
                                                }}
                                            />
                                        </div>

                                        <div className="mt-3 flex justify-between text-[10px] font-bold uppercase tracking-wider text-white/40">
                                            <span>Application</span>
                                            <span>Onboarding</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-8 sm:p-10 lg:p-12">
                                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B1E3F]">
                                        {admissionSteps[activeAdmissionStep].label}
                                    </span>

                                    <div className="mt-7 grid gap-4">
                                        {admissionSteps[activeAdmissionStep].items.map(
                                            (item, index) => (
                                                <div
                                                    key={item}
                                                    className="flex items-center gap-5 rounded-2xl border border-[#071A2F]/10 bg-[#F7F2E8]/60 p-5"
                                                >
                                                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#8B1E3F] text-xs font-bold text-white">
                                                        0{index + 1}
                                                    </span>
                                                    <strong className="text-sm text-[#071A2F] sm:text-base">
                                                        {item}
                                                    </strong>
                                                </div>
                                            )
                                        )}
                                    </div>

                                    <div className="mt-8 flex flex-wrap gap-3">
                                        <button
                                            type="button"
                                            disabled={activeAdmissionStep === 0}
                                            onClick={() =>
                                                setActiveAdmissionStep((current) =>
                                                    Math.max(0, current - 1)
                                                )
                                            }
                                            className="rounded-full border border-[#071A2F]/15 px-6 py-3 text-sm font-semibold text-[#071A2F] transition hover:border-[#8B1E3F] disabled:cursor-not-allowed disabled:opacity-30"
                                        >
                                            Previous
                                        </button>

                                        <button
                                            type="button"
                                            disabled={
                                                activeAdmissionStep === admissionSteps.length - 1
                                            }
                                            onClick={() =>
                                                setActiveAdmissionStep((current) =>
                                                    Math.min(admissionSteps.length - 1, current + 1)
                                                )
                                            }
                                            className="inline-flex items-center gap-3 rounded-full bg-[#8B1E3F] px-6 py-3 text-sm font-semibold text-white transition hover:-translate-y-1 hover:bg-[#071A2F] disabled:cursor-not-allowed disabled:opacity-30"
                                        >
                                            Next stage
                                            <ArrowIcon />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Eligibility */}
                        <div className="mt-16">
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                                <div>
                                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B1E3F]">
                                        Entry requirements
                                    </span>
                                    <h3
                                        className="mt-3 text-3xl font-normal text-[#071A2F] sm:text-4xl"
                                        style={{
                                            fontFamily: '"Libre Caslon Display", Georgia, serif',
                                        }}
                                    >
                                        Find your academic pathway.
                                    </h3>
                                </div>

                                <a
                                    href="/programmes"
                                    className="inline-flex items-center gap-2 text-sm font-bold text-[#8B1E3F]"
                                >
                                    View all programmes
                                    <ArrowIcon />
                                </a>
                            </div>

                            <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                                {eligibilityCards.map((item) => (
                                    <article
                                        key={item.code}
                                        className="group rounded-[24px] border border-[#071A2F]/10 bg-white p-6 transition duration-500 hover:-translate-y-2 hover:border-[#D5AD57] hover:shadow-[0_24px_55px_rgba(7,26,47,0.10)]"
                                    >
                                        <span className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl bg-[#071A2F] px-3 text-xs font-bold text-[#D5AD57]">
                                            {item.code}
                                        </span>

                                        <h4 className="mt-6 text-lg font-semibold text-[#071A2F]">
                                            {item.title}
                                        </h4>

                                        <p className="mt-4 text-sm font-medium leading-6 text-[#8B1E3F]">
                                            {item.eligibility}
                                        </p>

                                        <p className="mt-4 border-t border-[#071A2F]/10 pt-4 text-xs leading-5 text-[#132337]/50">
                                            {item.note}
                                        </p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/*  STUDENT TESTIMONIALS & SUCCESS STORIES  */}
                <Alumni />

                {/*  FAQ  */}
                <section
                    className="relative overflow-hidden bg-white py-20 sm:py-24 lg:py-32"
                    aria-labelledby="it-faq-heading"
                >
                    <div
                        className="pointer-events-none absolute inset-0 opacity-[0.025]"
                        style={{
                            backgroundImage:
                                "linear-gradient(#071A2F 1px, transparent 1px), linear-gradient(90deg, #071A2F 1px, transparent 1px)",
                            backgroundSize: "42px 42px",
                        }}
                    />

                    <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                        <div className="max-w-4xl">
                            <SectionEyebrow>Frequently asked questions</SectionEyebrow>

                            <h2
                                id="it-faq-heading"
                                className="mt-6 text-[clamp(42px,5vw,72px)] font-normal leading-[1.03] tracking-[-0.045em] text-[#071A2F]"
                                style={{
                                    fontFamily: '"Libre Caslon Display", Georgia, serif',
                                }}
                            >
                                The information you need,
                                <span className="italic text-[#8B1E3F]">
                                    {" "}
                                    clearly connected.
                                </span>
                            </h2>
                        </div>

                        <div className="mt-14 grid overflow-hidden rounded-[32px] border border-[#071A2F]/10 bg-white shadow-[0_30px_90px_rgba(7,26,47,0.10)] lg:grid-cols-[0.9fr_1.1fr]">
                            <div
                                className="border-b border-[#071A2F]/10 bg-[#F7F2E8]/70 p-4 sm:p-6 lg:border-b-0 lg:border-r"
                                role="tablist"
                                aria-label="Frequently asked questions"
                            >
                                {faqItems.map((faq, index) => {
                                    const isActive = activeFaq === index;

                                    return (
                                        <button
                                            key={faq.question}
                                            type="button"
                                            role="tab"
                                            aria-selected={isActive}
                                            onClick={() => setActiveFaq(index)}
                                            className={`group mb-2 flex w-full items-center gap-4 rounded-2xl p-4 text-left transition duration-300 last:mb-0 ${isActive
                                                ? "bg-[#071A2F] text-white shadow-lg"
                                                : "text-[#071A2F] hover:bg-white"
                                                }`}
                                        >
                                            <span
                                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${isActive
                                                    ? "bg-[#D5AD57] text-[#071A2F]"
                                                    : "border border-[#071A2F]/10 bg-white"
                                                    }`}
                                            >
                                                {String(index + 1).padStart(2, "0")}
                                            </span>

                                            <span className="min-w-0">
                                                <small
                                                    className={`block text-[9px] font-bold uppercase tracking-[0.16em] ${isActive ? "text-[#D5AD57]" : "text-[#8B1E3F]"
                                                        }`}
                                                >
                                                    {faq.category}
                                                </small>
                                                <strong className="mt-1 block truncate text-sm font-semibold">
                                                    {faq.question}
                                                </strong>
                                            </span>

                                            <ArrowIcon
                                                className={`ml-auto h-4 w-4 shrink-0 transition duration-300 ${isActive
                                                    ? "text-[#D5AD57]"
                                                    : "text-[#071A2F]/30 group-hover:translate-x-1"
                                                    }`}
                                            />
                                        </button>
                                    );
                                })}
                            </div>

                            <div
                                className="relative flex min-h-[500px] items-center overflow-hidden p-7 sm:p-10 lg:p-14"
                                role="tabpanel"
                            >
                                <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full border border-[#D5AD57]/20" />
                                <div className="absolute -right-8 -top-8 h-48 w-48 rounded-full border border-[#8B1E3F]/10" />

                                <div className="relative">
                                    <span className="inline-flex rounded-full bg-[#8B1E3F]/10 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#8B1E3F]">
                                        {faqItems[activeFaq].category}
                                    </span>

                                    <span
                                        className="mt-8 block text-7xl leading-none text-[#D5AD57]"
                                        style={{
                                            fontFamily: '"Libre Caslon Display", Georgia, serif',
                                        }}
                                    >
                                        ?
                                    </span>

                                    <h3
                                        className="mt-4 max-w-2xl text-3xl font-normal leading-tight text-[#071A2F] sm:text-4xl"
                                        style={{
                                            fontFamily: '"Libre Caslon Display", Georgia, serif',
                                        }}
                                    >
                                        {faqItems[activeFaq].question}
                                    </h3>

                                    <p className="mt-7 max-w-2xl text-base leading-8 text-[#132337]/65">
                                        {faqItems[activeFaq].answer}
                                    </p>

                                    <div className="mt-9 flex flex-wrap items-center gap-4">
                                        <a
                                            href="/contact-us"
                                            className="inline-flex items-center gap-3 rounded-full bg-[#8B1E3F] px-6 py-3.5 text-sm font-bold text-white transition duration-300 hover:-translate-y-1 hover:bg-[#071A2F]"
                                        >
                                            Ask admissions
                                            <ArrowIcon />
                                        </a>

                                        <span className="text-xs text-[#132337]/45">
                                            Need programme-specific guidance?
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/*  LEAD CAPTURE & CONTACT  */}
                <section
                    className="relative overflow-hidden bg-[#071A2F] py-20 text-white sm:py-24 lg:py-32"
                    aria-labelledby="it-enquiry-heading"
                >
                    <div className="pointer-events-none absolute inset-0">
                        <div className="absolute left-[-10%] top-[-30%] h-[600px] w-[600px] rounded-full bg-[#8B1E3F]/30 blur-[130px]" />
                        <div className="absolute bottom-[-40%] right-[-10%] h-[650px] w-[650px] rounded-full bg-[#D5AD57]/15 blur-[130px]" />

                        <div
                            className="absolute inset-0 opacity-[0.05]"
                            style={{
                                backgroundImage:
                                    "linear-gradient(#D5AD57 1px, transparent 1px), linear-gradient(90deg, #D5AD57 1px, transparent 1px)",
                                backgroundSize: "52px 52px",
                            }}
                        />
                    </div>

                    <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
                        <div className="overflow-hidden rounded-[36px] border border-white/10 bg-white/[0.055] shadow-[0_40px_120px_rgba(0,0,0,0.30)] backdrop-blur-xl">
                            <div className="grid lg:grid-cols-[0.85fr_1.15fr]">
                                {/* Contact content */}
                                <div className="relative overflow-hidden border-b border-white/10 p-7 sm:p-10 lg:border-b-0 lg:border-r lg:p-14">
                                    <SectionEyebrow light>Admissions 2026</SectionEyebrow>

                                    <h2
                                        id="it-enquiry-heading"
                                        className="mt-7 text-[clamp(42px,5vw,68px)] font-normal leading-[1.03] tracking-[-0.045em] text-white"
                                        style={{
                                            fontFamily: '"Libre Caslon Display", Georgia, serif',
                                        }}
                                    >
                                        Build what the future
                                        <span className="block italic text-[#D5AD57]">
                                            will depend upon.
                                        </span>
                                    </h2>

                                    <p className="mt-7 max-w-lg text-base leading-8 text-white/60">
                                        Speak with our admissions team and discover programmes
                                        designed for future-focused careers in technology,
                                        innovation and research.
                                    </p>

                                    <div className="mt-9 flex flex-wrap gap-3">
                                        <a
                                            href="/apply-online"
                                            className="inline-flex items-center gap-3 rounded-full bg-[#D5AD57] px-7 py-4 text-sm font-bold text-[#071A2F] transition duration-300 hover:-translate-y-1 hover:bg-white"
                                        >
                                            Apply now
                                            <ArrowIcon />
                                        </a>

                                        <a
                                            href="/brochure.pdf"
                                            className="inline-flex items-center gap-3 rounded-full border border-white/20 bg-white/[0.06] px-7 py-4 text-sm font-bold text-white transition duration-300 hover:border-white hover:bg-white hover:text-[#071A2F]"
                                        >
                                            Download brochure
                                            <span aria-hidden="true">↓</span>
                                        </a>
                                    </div>

                                    <div className="mt-12 grid gap-4 sm:grid-cols-2">
                                        {[
                                            ["Call admissions", "+91-9373199999", "tel:+919373199999"],
                                            [
                                                "Email us",
                                                "info@isbmuniversity.edu.in",
                                                "mailto:info@isbmuniversity.edu.in",
                                            ],
                                            [
                                                "WhatsApp support",
                                                "Chat with our team",
                                                "https://wa.me/919373199999",
                                            ],
                                            [
                                                "Campus",
                                                "Chhattisgarh, India",
                                                "/contact-us",
                                            ],
                                        ].map(([label, value, link]) => (
                                            <a
                                                key={label}
                                                href={link}
                                                className="group rounded-2xl border border-white/10 bg-white/[0.045] p-5 transition duration-300 hover:-translate-y-1 hover:border-[#D5AD57]/50 hover:bg-white/[0.08]"
                                            >
                                                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#D5AD57]">
                                                    {label}
                                                </span>
                                                <strong className="mt-2 block break-words text-sm text-white/80">
                                                    {value}
                                                </strong>
                                            </a>
                                        ))}
                                    </div>
                                </div>

                                {/* Enquiry form */}
                                <div className="bg-white p-7 text-[#071A2F] sm:p-10 lg:p-14">
                                    <div className="flex items-start justify-between gap-6">
                                        <div>
                                            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B1E3F]">
                                                Admission enquiry
                                            </span>
                                            <h3
                                                className="mt-3 text-3xl font-normal sm:text-4xl"
                                                style={{
                                                    fontFamily: '"Libre Caslon Display", Georgia, serif',
                                                }}
                                            >
                                                Let&apos;s plan your next step.
                                            </h3>
                                        </div>

                                        <span className="hidden h-14 w-14 items-center justify-center rounded-2xl bg-[#071A2F] text-[#D5AD57] sm:flex">
                                            <ArrowIcon className="h-5 w-5 -rotate-45" />
                                        </span>
                                    </div>

                                    <form
                                        className="mt-9 grid gap-5 sm:grid-cols-2"
                                        onSubmit={(event) => event.preventDefault()}
                                    >
                                        <label className="block">
                                            <span className="mb-2 block text-xs font-bold text-[#071A2F]">
                                                Full name *
                                            </span>
                                            <input
                                                type="text"
                                                name="name"
                                                required
                                                autoComplete="name"
                                                placeholder="Enter your name"
                                                className="w-full rounded-2xl border border-[#071A2F]/10 bg-[#F7F2E8]/50 px-5 py-4 text-sm outline-none transition placeholder:text-[#132337]/35 focus:border-[#8B1E3F] focus:bg-white focus:ring-4 focus:ring-[#8B1E3F]/10"
                                            />
                                        </label>

                                        <label className="block">
                                            <span className="mb-2 block text-xs font-bold text-[#071A2F]">
                                                Mobile number *
                                            </span>
                                            <input
                                                type="tel"
                                                name="phone"
                                                required
                                                autoComplete="tel"
                                                placeholder="+91"
                                                className="w-full rounded-2xl border border-[#071A2F]/10 bg-[#F7F2E8]/50 px-5 py-4 text-sm outline-none transition placeholder:text-[#132337]/35 focus:border-[#8B1E3F] focus:bg-white focus:ring-4 focus:ring-[#8B1E3F]/10"
                                            />
                                        </label>

                                        <label className="block">
                                            <span className="mb-2 block text-xs font-bold text-[#071A2F]">
                                                Email address *
                                            </span>
                                            <input
                                                type="email"
                                                name="email"
                                                required
                                                autoComplete="email"
                                                placeholder="you@example.com"
                                                className="w-full rounded-2xl border border-[#071A2F]/10 bg-[#F7F2E8]/50 px-5 py-4 text-sm outline-none transition placeholder:text-[#132337]/35 focus:border-[#8B1E3F] focus:bg-white focus:ring-4 focus:ring-[#8B1E3F]/10"
                                            />
                                        </label>

                                        <label className="block">
                                            <span className="mb-2 block text-xs font-bold text-[#071A2F]">
                                                Programme level *
                                            </span>
                                            <select
                                                name="level"
                                                required
                                                defaultValue=""
                                                className="w-full rounded-2xl border border-[#071A2F]/10 bg-[#F7F2E8]/50 px-5 py-4 text-sm outline-none transition focus:border-[#8B1E3F] focus:bg-white focus:ring-4 focus:ring-[#8B1E3F]/10"
                                            >
                                                <option value="" disabled>
                                                    Select level
                                                </option>
                                                <option>Undergraduate</option>
                                                <option>Postgraduate</option>
                                                <option>Diploma</option>
                                                <option>Doctoral</option>
                                            </select>
                                        </label>

                                        <label className="block sm:col-span-2">
                                            <span className="mb-2 block text-xs font-bold text-[#071A2F]">
                                                Programme interest *
                                            </span>
                                            <select
                                                name="programme"
                                                required
                                                defaultValue=""
                                                className="w-full rounded-2xl border border-[#071A2F]/10 bg-[#F7F2E8]/50 px-5 py-4 text-sm outline-none transition focus:border-[#8B1E3F] focus:bg-white focus:ring-4 focus:ring-[#8B1E3F]/10"
                                            >
                                                <option value="" disabled>
                                                    Select a programme
                                                </option>
                                                <option>Computer Science</option>
                                                <option>Information Technology</option>
                                                <option>Artificial Intelligence</option>
                                                <option>Machine Learning</option>
                                                <option>Data Science</option>
                                                <option>Cybersecurity</option>
                                                <option>Cloud Computing</option>
                                                <option>Software Engineering</option>
                                            </select>
                                        </label>

                                        <label className="flex items-start gap-3 sm:col-span-2">
                                            <input
                                                type="checkbox"
                                                required
                                                className="mt-1 h-4 w-4 rounded border-[#071A2F]/20 text-[#8B1E3F] focus:ring-[#8B1E3F]"
                                            />
                                            <span className="text-xs leading-5 text-[#132337]/55">
                                                I agree to receive admission-related communication from
                                                ISBM University.
                                            </span>
                                        </label>

                                        <button
                                            type="submit"
                                            className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#8B1E3F] px-8 py-4 text-sm font-bold text-white transition duration-300 hover:-translate-y-1 hover:bg-[#071A2F] hover:shadow-[0_18px_40px_rgba(139,30,63,0.24)] sm:col-span-2"
                                        >
                                            Submit enquiry
                                            <ArrowIcon />
                                        </button>
                                    </form>

                                    <p className="mt-5 text-center text-[11px] leading-5 text-[#132337]/40">
                                        Our admissions team will contact you to provide
                                        programme-specific guidance.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Social/contact footer strip */}
                        <div className="mt-8 flex flex-col gap-5 border-t border-white/10 pt-7 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-xs text-white/40">
                                School of Information Technology · ISBM University
                            </p>

                            <div className="flex flex-wrap gap-5">
                                {["LinkedIn", "Instagram", "Facebook", "YouTube"].map(
                                    (platform) => (
                                        <a
                                            key={platform}
                                            href="/"
                                            aria-label={platform}
                                            className="text-xs font-bold text-white/55 transition hover:text-[#D5AD57]"
                                        >
                                            {platform}
                                        </a>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}

export default IT;

